/**
 * CCNP ENCOR 350-401 Comparison Matrix Sets
 * All values verified against IOS-XE and ENCOR 350-401 v1.1 blueprint.
 */

// Increment this when adding/modifying sets so auto-merge picks up changes
export const CONTENT_VERSION = 6;

export function getComparisonSets() {
  return [
    // -------------------------------------------------------------------------
    // cmp-001  OSPF vs EIGRP vs BGP
    // -------------------------------------------------------------------------
    {
      id: "cmp-001",
      title: "OSPF vs EIGRP vs BGP",
      domain: "3.0",
      protocols: ["OSPF", "EIGRP", "BGP"],
      is_custom: false,
      attributes: [
        {
          name: "Type",
          values: {
            OSPF: "Link-state",
            EIGRP: "Advanced distance vector",
            BGP: "Path vector",
          },
          exam_hot: true,
          notes: {
            OSPF: "Link-state means every router builds a complete topology map using LSAs and runs SPF",
            EIGRP: "Cisco calls EIGRP 'advanced distance vector' on the exam — know this exact term",
            BGP: "Path vector means routes carry the full AS-path, enabling loop detection and policy decisions",
          },
        },
        {
          name: "Algorithm",
          values: {
            OSPF: "Dijkstra (SPF)",
            EIGRP: "DUAL (Diffusing Update Algorithm)",
            BGP: "Best-path selection (policy-based)",
          },
          exam_hot: true,
          notes: {
            OSPF: "SPF runs per-area; partial SPF recalculation (incremental SPF) reduces CPU on topology changes",
            EIGRP: "DUAL guarantees loop-free paths at every instant — no holddown timers needed",
            BGP: "BGP best-path walks through up to 13 selection steps; it is policy-based, not metric-based",
          },
        },
        {
          name: "Administrative Distance (internal)",
          values: {
            OSPF: "110",
            EIGRP: "90",
            BGP: "20 (eBGP) / 200 (iBGP)",
          },
          exam_hot: true,
          notes: {
            OSPF: "AD 110 applies to all OSPF route types (intra-area, inter-area, and external)",
            EIGRP: "AD 90 for internal EIGRP is lower than OSPF 110 — EIGRP wins when both are present",
            BGP: "iBGP AD of 200 is often overlooked; eBGP AD of 20 is the lowest of any routing protocol",
          },
        },
        {
          name: "Administrative Distance (external)",
          values: {
            OSPF: "110 (Type 5/7 LSAs still AD 110)",
            EIGRP: "170",
            BGP: "20 (eBGP) / 200 (iBGP)",
          },
          exam_hot: true,
          notes: {
            OSPF: "OSPF external routes (E1/E2) still use AD 110 — same as internal OSPF routes",
            EIGRP: "EIGRP external routes use AD 170, making them less preferred than internal routes (AD 90)",
            BGP: "BGP AD does not change for external vs internal prefixes; eBGP is always 20, iBGP always 200",
          },
        },
        {
          name: "Metric",
          values: {
            OSPF: "Cost (reference BW / interface BW)",
            EIGRP: "Composite (BW + delay by default; load, reliability, MTU optional)",
            BGP: "Not a metric-based protocol; uses path attributes",
          },
          exam_hot: true,
          notes: {
            OSPF: "Reference bandwidth defaults to 100 Mbps; tune with 'auto-cost reference-bandwidth' for 10G+ links",
            EIGRP: "Default K-values: K1=1 (BW), K2=0, K3=1 (delay), K4=0, K5=0 — only BW and delay matter by default",
            BGP: "BGP does not use a traditional metric; path selection is driven by attributes (Weight, LP, AS-path, MED, etc.)",
          },
        },
        {
          name: "Hello timer (default)",
          values: {
            OSPF: "10 s (broadcast/p2p), 30 s (NBMA)",
            EIGRP: "5 s (LAN), 60 s (WAN <T1)",
            BGP: "60 s (keepalive)",
          },
          exam_hot: true,
          notes: {
            OSPF: "OSPF dead timer = 4x hello; mismatched timers prevent adjacency formation",
            EIGRP: "EIGRP hold timer = 3x hello; 5s hello on LAN means 15s hold time",
            BGP: "BGP keepalive of 60s is much slower than IGP hellos; hold time is 180s (3x keepalive)",
          },
        },
        {
          name: "Dead / Hold timer (default)",
          values: {
            OSPF: "40 s (broadcast/p2p), 120 s (NBMA)",
            EIGRP: "15 s (LAN), 180 s (WAN <T1)",
            BGP: "180 s (hold time)",
          },
          exam_hot: true,
          notes: {
            OSPF: "Dead timer mismatch prevents adjacency; use 'ip ospf dead-interval' to change",
            EIGRP: "Hold timer can be configured independently from hello; neighbor drops if no hello received within hold time",
            BGP: "Hold time of 180s means a BGP peer failure can take 3 minutes to detect without BFD",
          },
        },
        {
          name: "Transport / Protocol number",
          values: {
            OSPF: "IP protocol 89",
            EIGRP: "IP protocol 88",
            BGP: "TCP port 179",
          },
          exam_hot: true,
          notes: {
            OSPF: "OSPF runs directly over IP (protocol 89) — no TCP/UDP layer",
            EIGRP: "EIGRP runs directly over IP (protocol 88) with its own reliable transport (RTP)",
            BGP: "BGP is the only major routing protocol that uses TCP — this enables neighbor relationships across multiple hops",
          },
        },
        {
          name: "Multicast address",
          values: {
            OSPF: "224.0.0.5 (all OSPF routers), 224.0.0.6 (DR/BDR)",
            EIGRP: "224.0.0.10",
            BGP: "Unicast TCP sessions only",
          },
          exam_hot: true,
          notes: {
            OSPF: "224.0.0.5 for all OSPF routers; 224.0.0.6 used only by non-DR routers to reach the DR/BDR",
            EIGRP: "224.0.0.10 is the single multicast address for all EIGRP messages",
            BGP: "BGP uses unicast TCP sessions only — no multicast; neighbors must be explicitly configured",
          },
        },
        {
          name: "Authentication options",
          values: {
            OSPF: "MD5, SHA (OSPFv3 uses IPsec)",
            EIGRP: "MD5, SHA-256 (named mode)",
            BGP: "MD5 (TCP MD5 option, RFC 2385)",
          },
          exam_hot: false,
          notes: {
            OSPF: "OSPFv2 supports MD5 per-interface; OSPFv3 uses IPsec for both authentication and encryption",
            EIGRP: "SHA-256 is only available in named mode — classic mode is limited to MD5",
            BGP: "BGP MD5 authentication protects the TCP session; TCP-AO (RFC 5925) is the modern replacement but less common on IOS-XE",
          },
        },
        {
          name: "Area / AS concept",
          values: {
            OSPF: "Areas (hierarchical; backbone area 0 required)",
            EIGRP: "Autonomous Systems (flat; optional named mode)",
            BGP: "AS numbers (16-bit or 32-bit)",
          },
          exam_hot: true,
          notes: {
            OSPF: "Area 0 is mandatory for inter-area routing; all other areas must connect to area 0 (directly or via virtual link)",
            EIGRP: "EIGRP AS is flat — no hierarchy; named mode uses a virtual instance name but still operates as a flat topology",
            BGP: "32-bit ASNs (RFC 6793) expand the AS range; private ASNs: 64512–65534 (16-bit), 4200000000–4294967294 (32-bit)",
          },
        },
        {
          name: "Convergence speed",
          values: {
            OSPF: "Moderate (SPF recalculation)",
            EIGRP: "Fast (DUAL; uses feasible successors)",
            BGP: "Slow (policy convergence; not designed for fast failover)",
          },
          exam_hot: true,
          notes: {
            OSPF: "OSPF convergence depends on SPF delay timers (default: 5s initial, 10s subsequent); tuning helps but still moderate",
            EIGRP: "Convergence is near-instant when a feasible successor exists — no SPF recalculation needed",
            BGP: "BGP convergence is intentionally slow; MinRouteAdvertisementInterval and scanner timer add delay for stability",
          },
        },
        {
          name: "Update mechanism",
          values: {
            OSPF: "Flooding (LSAs); full DB on adjacency, partial updates on change",
            EIGRP: "Bounded updates (only to affected neighbors)",
            BGP: "Incremental updates after full table on session establishment",
          },
          exam_hot: false,
          notes: {
            OSPF: "LSA flooding scope depends on LSA type; MinLSInterval and MinLSArrival throttle flooding",
            EIGRP: "Bounded updates only go to neighbors affected by the topology change, reducing bandwidth usage",
            BGP: "BGP sends a full table dump on session establishment, then incremental updates for changes only",
          },
        },
        {
          name: "VLSM / CIDR support",
          values: {
            OSPF: "Yes (classless)",
            EIGRP: "Yes (classless)",
            BGP: "Yes (classless; CIDR native)",
          },
          exam_hot: false,
          notes: {
            OSPF: "",
            EIGRP: "",
            BGP: "",
          },
        },
        {
          name: "Default route injection",
          values: {
            OSPF: "default-information originate [always]",
            EIGRP: "ip default-network or redistribute static",
            BGP: "network 0.0.0.0 or default-information originate",
          },
          exam_hot: true,
          notes: {
            OSPF: "The 'always' keyword injects a default even without a default route in the RIB — critical exam distinction",
            EIGRP: "'ip default-network' marks a classful network as a candidate default; 'redistribute static' with a static default is more common",
            BGP: "BGP 'default-information originate' sends a default to a specific neighbor; 'network 0.0.0.0' requires the route in the RIB",
          },
        },
        {
          name: "Summarization",
          values: {
            OSPF: "ABR (inter-area) or ASBR (external); not on internal routers",
            EIGRP: "Any router (auto-summary or manual 'ip summary-address')",
            BGP: "aggregate-address command",
          },
          exam_hot: true,
          notes: {
            OSPF: "OSPF summarization is only at ABR (area range) or ASBR (summary-address); internal routers cannot summarize",
            EIGRP: "auto-summary is disabled by default in IOS 15+; manual summary via 'ip summary-address eigrp' on any interface",
            BGP: "aggregate-address can suppress specifics with 'summary-only'; as-set preserves path info in the aggregate",
          },
        },
        {
          name: "Neighbor requirements",
          values: {
            OSPF: "Same area, subnet, MTU, hello/dead timers, auth, stub flag",
            EIGRP: "Same AS#, K-values, auth (if configured)",
            BGP: "Explicitly configured; must be reachable via TCP 179",
          },
          exam_hot: true,
          notes: {
            OSPF: "MTU mismatch causes stuck-in-EXSTART/EXCHANGE — a very common exam troubleshooting scenario",
            EIGRP: "K-value mismatch prevents adjacency; use 'show ip eigrp interfaces detail' to verify",
            BGP: "BGP neighbors must be explicitly configured with 'neighbor <IP> remote-as'; eBGP requires reachability (often via 'ebgp-multihop' or 'update-source')",
          },
        },
        {
          name: "Load balancing",
          values: {
            OSPF: "Equal-cost only (default 4, max 16 paths)",
            EIGRP: "Equal and unequal-cost (variance command)",
            BGP: "Equal-cost multipath (requires same AS-path length by default)",
          },
          exam_hot: true,
          notes: {
            OSPF: "Adjust max paths with 'maximum-paths' under router ospf; default is 4 equal-cost paths",
            EIGRP: "Unequal-cost load balancing with 'variance' multiplier is a top exam topic — only feasible successors qualify",
            BGP: "BGP multipath requires 'maximum-paths' config; eiBGP multipath available with 'maximum-paths eibgp'",
          },
        },
        {
          name: "Route types in table",
          values: {
            OSPF: "O, O IA, O E1, O E2, O N1, O N2",
            EIGRP: "D (internal), D EX (external)",
            BGP: "B",
          },
          exam_hot: false,
          notes: {
            OSPF: "E2 is default for external routes (cost does not accumulate); E1 adds internal cost to external metric",
            EIGRP: "D EX routes have AD 170; internal D routes have AD 90 — the AD difference matters in redistribution scenarios",
            BGP: "BGP routes show as 'B' in the routing table; use 'show ip bgp' for detailed path attributes",
          },
        },
        {
          name: "Wildcard vs subnet mask",
          values: {
            OSPF: "Wildcard mask in 'network' statement",
            EIGRP: "Wildcard mask in 'network' statement (or exact /32 with no mask)",
            BGP: "Exact subnet mask in 'network' statement",
          },
          exam_hot: true,
          notes: {
            OSPF: "OSPF 'network' command uses wildcard mask to match interfaces, not to advertise specific subnets",
            EIGRP: "EIGRP 'network' also uses wildcard mask to activate EIGRP on matching interfaces",
            BGP: "BGP 'network' command requires an exact match in the RIB — if the prefix/mask doesn't match exactly, it won't be advertised",
          },
        },
      ],
    },

    // -------------------------------------------------------------------------
    // cmp-002  OSPF Area Types
    // -------------------------------------------------------------------------
    {
      id: "cmp-002",
      title: "OSPF Area Types",
      domain: "3.0",
      protocols: ["Stub", "Totally Stubby", "NSSA", "Totally NSSA"],
      is_custom: false,
      attributes: [
        {
          name: "LSA types allowed in",
          values: {
            Stub: "1, 2, 3 (Type 4 & 5 blocked)",
            "Totally Stubby": "1, 2 only (Type 3, 4, 5 blocked; ABR injects default Type 3)",
            NSSA: "1, 2, 3, 7 (Type 5 blocked; external routes use Type 7)",
            "Totally NSSA": "1, 2, 7 only (Type 3, 4, 5 blocked; default via Type 7 or Type 3)",
          },
          exam_hot: true,
          notes: {
            Stub: "Stub areas block Type 4 and 5 LSAs; Type 3 summaries are still allowed through the ABR",
            "Totally Stubby": "Only Type 1 and 2 LSAs plus a single default Type 3 — the most restrictive non-NSSA area type",
            NSSA: "Type 7 LSAs carry external routes within the NSSA; they are converted to Type 5 at the ABR",
            "Totally NSSA": "Combines NSSA external capability (Type 7) with totally stubby filtering (no Type 3/4/5 except default)",
          },
        },
        {
          name: "Default route injected?",
          values: {
            Stub: "Yes — Type 3 LSA from ABR automatically",
            "Totally Stubby": "Yes — Type 3 LSA from ABR automatically",
            NSSA: "Not automatic; requires 'default-information-originate' on ASBR",
            "Totally NSSA": "Yes — Type 7 default from ABR (Cisco) or Type 3",
          },
          exam_hot: true,
          notes: {
            Stub: "ABR automatically injects a default Type 3 LSA into stub areas",
            "Totally Stubby": "Same automatic default injection as stub; the ABR suppresses all other Type 3 LSAs",
            NSSA: "NSSA does NOT automatically get a default route — this is the key gotcha; you must explicitly configure it",
            "Totally NSSA": "ABR injects a default route; Cisco IOS uses Type 3 default, some implementations use Type 7",
          },
        },
        {
          name: "External (redistributed) routes",
          values: {
            Stub: "Not allowed",
            "Totally Stubby": "Not allowed",
            NSSA: "Allowed as Type 7 LSA",
            "Totally NSSA": "Allowed as Type 7 LSA",
          },
          exam_hot: true,
          notes: {
            Stub: "No ASBR can exist in a stub area — redistribution is completely blocked",
            "Totally Stubby": "Same as stub — no external routes allowed; even more restrictive with inter-area filtering",
            NSSA: "NSSA was created specifically to allow redistribution in stub-like areas via Type 7 LSAs",
            "Totally NSSA": "External routes allowed as Type 7 but inter-area Type 3 summaries are blocked (except default)",
          },
        },
        {
          name: "ABR behavior",
          values: {
            Stub: "Blocks Type 4 & 5 from entering; injects default Type 3",
            "Totally Stubby": "Blocks Type 3, 4, 5; injects only default Type 3",
            NSSA: "Translates Type 7 to Type 5 at ABR (highest RID translates)",
            "Totally NSSA": "Translates Type 7 to Type 5; suppresses inter-area Type 3 (except default)",
          },
          exam_hot: true,
          notes: {
            Stub: "ABR filters Type 4/5 and generates a default Type 3 LSA with metric 1 by default",
            "Totally Stubby": "ABR filters Type 3/4/5 — only the default Type 3 passes; configured with 'no-summary' on ABR only",
            NSSA: "Only the NSSA ABR with the highest Router ID performs Type 7-to-5 translation — a key exam fact",
            "Totally NSSA": "ABR performs both Type 7-to-5 translation and Type 3 suppression; 'no-summary' on ABR",
          },
        },
        {
          name: "ASBR allowed in area?",
          values: {
            Stub: "No",
            "Totally Stubby": "No",
            NSSA: "Yes",
            "Totally NSSA": "Yes",
          },
          exam_hot: true,
          notes: {
            Stub: "No redistribution allowed — any router performing redistribution cannot be in a stub area",
            "Totally Stubby": "Same restriction as stub — no ASBR permitted",
            NSSA: "NSSA's primary purpose is to allow an ASBR in a stub-like area",
            "Totally NSSA": "ASBR allowed; external routes carried as Type 7 within the area",
          },
        },
        {
          name: "Config command (router ospf)",
          values: {
            Stub: "area <id> stub",
            "Totally Stubby": "area <id> stub no-summary (on ABR only)",
            NSSA: "area <id> nssa",
            "Totally NSSA": "area <id> nssa no-summary (on ABR only)",
          },
          exam_hot: true,
          notes: {
            Stub: "All routers in the area must have 'area <id> stub' configured — stub flag must match for adjacency",
            "Totally Stubby": "'no-summary' is only configured on the ABR; internal routers just use 'area <id> stub'",
            NSSA: "All routers in the NSSA must have 'area <id> nssa'; the stub flag in hello packets must match",
            "Totally NSSA": "'no-summary' on ABR only; other routers configure 'area <id> nssa' — same pattern as totally stubby",
          },
        },
      ],
    },

    // -------------------------------------------------------------------------
    // cmp-003  OSPF LSA Types 1–7
    // -------------------------------------------------------------------------
    {
      id: "cmp-003",
      title: "OSPF LSA Types 1–7",
      domain: "3.0",
      protocols: ["Type 1", "Type 2", "Type 3", "Type 4", "Type 5", "Type 6", "Type 7"],
      is_custom: false,
      attributes: [
        {
          name: "Name",
          values: {
            "Type 1": "Router LSA",
            "Type 2": "Network LSA",
            "Type 3": "Summary (Network) LSA",
            "Type 4": "Summary (ASBR) LSA",
            "Type 5": "AS External LSA",
            "Type 6": "Group Membership LSA (MOSPF — not used on IOS)",
            "Type 7": "NSSA External LSA",
          },
          exam_hot: true,
          notes: {
            "Type 1": "Every OSPF router generates exactly one Type 1 LSA per area it belongs to",
            "Type 2": "Only generated on multi-access segments (broadcast/NBMA) where a DR exists",
            "Type 3": "Created by ABRs to advertise inter-area prefixes — not a true summary, more of a distance vector advertisement",
            "Type 4": "Provides reachability information to ASBRs in other areas — generated by ABR, not the ASBR itself",
            "Type 5": "Carries redistributed external routes throughout the OSPF domain (except stub/NSSA areas)",
            "Type 6": "Not supported on Cisco IOS and not tested on ENCOR — safe to ignore",
            "Type 7": "NSSA equivalent of Type 5; converted to Type 5 at the NSSA ABR for distribution to other areas",
          },
        },
        {
          name: "Generated by",
          values: {
            "Type 1": "Every OSPF router",
            "Type 2": "Designated Router (DR) on multi-access segments",
            "Type 3": "Area Border Router (ABR)",
            "Type 4": "ABR (describes how to reach the ASBR)",
            "Type 5": "ASBR",
            "Type 6": "MOSPF router (not IOS)",
            "Type 7": "ASBR inside an NSSA",
          },
          exam_hot: true,
          notes: {
            "Type 1": "Each router generates one Router LSA per area; the LSA ID is the router's Router ID",
            "Type 2": "The DR generates the Network LSA; if no DR election occurs (point-to-point), no Type 2 is created",
            "Type 3": "ABR creates a new Type 3 LSA for each prefix it advertises into another area — it does not flood the original Type 1/2",
            "Type 4": "ABR generates Type 4 to tell other areas how to reach the ASBR; not needed in the ASBR's own area",
            "Type 5": "ASBR generates Type 5 for each redistributed external prefix; flooded domain-wide",
            "Type 6": "",
            "Type 7": "ASBR inside NSSA generates Type 7 instead of Type 5; ABR translates to Type 5 for the rest of the domain",
          },
        },
        {
          name: "Flooding scope",
          values: {
            "Type 1": "Intra-area only",
            "Type 2": "Intra-area only",
            "Type 3": "Inter-area (one area at a time; ABR generates per area)",
            "Type 4": "All areas except the ASBR's own area",
            "Type 5": "Entire OSPF domain (except stub/NSSA areas)",
            "Type 6": "Intra-area (MOSPF only)",
            "Type 7": "NSSA area only; translated to Type 5 at ABR",
          },
          exam_hot: true,
          notes: {
            "Type 1": "Type 1 LSAs never cross area boundaries — ABR generates Type 3 instead",
            "Type 2": "Type 2 stays within the area; used to build the SPF tree for multi-access segments",
            "Type 3": "ABR re-generates Type 3 into each connected area; this is area-scope flooding",
            "Type 4": "Type 4 is not needed in the ASBR's local area because the ASBR is reachable via Type 1/2",
            "Type 5": "Type 5 is AS-scope — flooded everywhere except stub and NSSA areas",
            "Type 6": "",
            "Type 7": "Type 7 stays within the NSSA; the ABR with highest RID translates it to Type 5 for other areas",
          },
        },
        {
          name: "Contents / describes",
          values: {
            "Type 1": "Router's links, states, and costs within the area",
            "Type 2": "Multi-access segment: DR IP and attached routers",
            "Type 3": "Inter-area network prefixes and cost to them",
            "Type 4": "Metric to reach the ASBR",
            "Type 5": "External routes (E1 or E2) redistributed into OSPF",
            "Type 6": "IP multicast group membership",
            "Type 7": "External routes inside NSSA (N1 or N2)",
          },
          exam_hot: false,
          notes: {
            "Type 1": "Contains all router links (stub networks, transit networks, point-to-point, virtual links) with their costs",
            "Type 2": "Lists all routers attached to the multi-access segment — enables transit network calculation in SPF",
            "Type 3": "Contains the prefix/mask and the ABR's cost to reach it — acts like a distance-vector advertisement",
            "Type 4": "Contains the metric from the ABR to the ASBR — needed so other areas can calculate total cost to external routes",
            "Type 5": "E2 routes use fixed external cost (default); E1 adds internal cost — E2 is the default type",
            "Type 6": "",
            "Type 7": "N1/N2 mirror E1/E2 behavior but within NSSA; N2 is the default external metric type",
          },
        },
        {
          name: "Where seen in topology",
          values: {
            "Type 1": "Every area",
            "Type 2": "Every broadcast/NBMA area with a DR",
            "Type 3": "All non-backbone areas (inter-area routes)",
            "Type 4": "All areas that need to reach an ASBR in another area",
            "Type 5": "All non-stub areas",
            "Type 6": "MOSPF environments only",
            "Type 7": "NSSA and Totally NSSA areas only",
          },
          exam_hot: true,
          notes: {
            "Type 1": "Type 1 LSAs exist in every area — they are the foundation of the OSPF LSDB",
            "Type 2": "Only present on broadcast/NBMA segments; point-to-point links do not generate Type 2",
            "Type 3": "Also seen in backbone area 0 when ABR advertises non-backbone prefixes into area 0",
            "Type 4": "Only created when an ASBR exists in a different area; not present in the ASBR's own area",
            "Type 5": "Blocked by stub and NSSA areas — this is the fundamental stub area concept",
            "Type 6": "",
            "Type 7": "Exists only within NSSA boundaries; converted to Type 5 when leaving NSSA via ABR",
          },
        },
      ],
    },

    // -------------------------------------------------------------------------
    // cmp-004  BGP Path Selection Attributes
    // -------------------------------------------------------------------------
    {
      id: "cmp-004",
      title: "BGP Path Selection Attributes",
      domain: "3.0",
      protocols: ["Weight", "Local Pref", "Locally Originated", "AS Path", "Origin", "MED"],
      is_custom: false,
      attributes: [
        {
          name: "Selection order (step)",
          values: {
            Weight: "1 (first)",
            "Local Pref": "2",
            "Locally Originated": "3",
            "AS Path": "4",
            Origin: "5",
            MED: "6",
          },
          exam_hot: true,
          notes: {
            Weight: "Weight is checked first — highest wins; Cisco-specific so it overrides all other attributes",
            "Local Pref": "Step 2: Local Preference — highest wins; shared with all iBGP peers in the AS",
            "Locally Originated": "Step 3: prefer locally originated routes (network, redistribute, aggregate) over learned routes",
            "AS Path": "Step 4: shortest AS-path length wins; can be manipulated with 'set as-path prepend'",
            Origin: "Step 5: IGP (i) < EGP (e) < Incomplete (?); rarely the tiebreaker in practice",
            MED: "Step 6: lowest MED wins; only compared between paths from the same neighboring AS by default",
          },
        },
        {
          name: "Scope",
          values: {
            Weight: "Local router only (Cisco proprietary)",
            "Local Pref": "Entire AS",
            "Locally Originated": "Prefer routes originated by this router",
            "AS Path": "AS-wide comparison; shorter = preferred",
            Origin: "IGP < EGP < Incomplete",
            MED: "Suggests preferred entry point to neighboring AS",
          },
          exam_hot: true,
          notes: {
            Weight: "Weight is never advertised to any BGP peer — it only affects the local router's decision",
            "Local Pref": "Advertised to all iBGP peers; not sent to eBGP peers — used to influence outbound traffic for the entire AS",
            "Locally Originated": "This is an automatic preference, not a configurable attribute — locally originated routes win at step 3",
            "AS Path": "AS-path is visible to all BGP peers and is the primary loop-prevention mechanism in BGP",
            Origin: "Origin code is set by how the route entered BGP: 'network' = IGP, 'redistribute' = Incomplete",
            MED: "MED is sent to eBGP peers to suggest the preferred inbound path; it is non-transitive (not passed beyond the next AS)",
          },
        },
        {
          name: "Default value",
          values: {
            Weight: "32768 (locally originated), 0 (learned)",
            "Local Pref": "100",
            "Locally Originated": "N/A (boolean check)",
            "AS Path": "Varies (length of AS-path list)",
            Origin: "IGP when using 'network'; Incomplete when redistributing",
            MED: "0 (if not set; some implementations treat missing as 0 or infinity)",
          },
          exam_hot: true,
          notes: {
            Weight: "Locally originated routes get weight 32768; all learned routes default to 0 — this is why local routes are preferred",
            "Local Pref": "Default 100; change with 'bgp default local-preference' or per-route in a route-map",
            "Locally Originated": "",
            "AS Path": "Each AS the route passes through adds its ASN; prepending artificially lengthens the path",
            Origin: "Using 'network' command sets origin to IGP (i); redistributing sets it to Incomplete (?)",
            MED: "Cisco treats missing MED as 0 by default; 'bgp bestpath med missing-as-worst' changes this behavior",
          },
        },
        {
          name: "Higher or lower preferred",
          values: {
            Weight: "Higher preferred",
            "Local Pref": "Higher preferred",
            "Locally Originated": "Locally originated preferred",
            "AS Path": "Shorter preferred",
            Origin: "Lower code preferred (IGP=i < EGP=e < Incomplete=?)",
            MED: "Lower preferred",
          },
          exam_hot: true,
          notes: {
            Weight: "Higher weight wins — set a high weight to prefer a specific path on this router only",
            "Local Pref": "Higher Local Pref wins — set higher LP on the preferred exit point for the AS",
            "Locally Originated": "",
            "AS Path": "Shorter AS-path wins — prepend your own ASN to make a path longer (less preferred)",
            Origin: "IGP (i) is most preferred; Incomplete (?) is least preferred — this is an ascending preference",
            MED: "Lower MED wins — opposite of Weight and Local Pref; the only commonly used attribute where lower is better (besides AS-path length)",
          },
        },
        {
          name: "Where configured",
          values: {
            Weight: "neighbor weight command or route-map on inbound",
            "Local Pref": "set local-preference in route-map; bgp default local-preference",
            "Locally Originated": "Not configured; automatic",
            "AS Path": "set as-path prepend in route-map (outbound to increase length)",
            Origin: "set origin in route-map",
            MED: "set metric in route-map (outbound to neighbors)",
          },
          exam_hot: true,
          notes: {
            Weight: "Weight can only be set on inbound — it's a local attribute; 'neighbor <IP> weight' or inbound route-map",
            "Local Pref": "Local Pref is set on inbound from eBGP and shared with iBGP; it influences outbound traffic from the AS",
            "Locally Originated": "Automatic check — no configuration needed; locally originated routes are preferred at step 3",
            "AS Path": "AS-path prepend is applied outbound to make your route look longer to upstream peers, influencing their inbound path selection",
            Origin: "Rarely manipulated directly; changing origin from incomplete to IGP can influence path selection at step 5",
            MED: "MED is set outbound to eBGP peers; it suggests to the neighboring AS which entry point to prefer for your prefixes",
          },
        },
        {
          name: "Cisco-specific?",
          values: {
            Weight: "Yes — Cisco proprietary",
            "Local Pref": "No — RFC 4271",
            "Locally Originated": "No — RFC 4271",
            "AS Path": "No — RFC 4271",
            Origin: "No — RFC 4271",
            MED: "No — RFC 4271",
          },
          exam_hot: true,
          notes: {
            Weight: "Weight is the only Cisco-proprietary attribute in the BGP best-path selection process — a frequent exam question",
            "Local Pref": "Defined in RFC 4271; well-known mandatory attribute",
            "Locally Originated": "Standard BGP behavior defined in RFC 4271",
            "AS Path": "Well-known mandatory attribute; fundamental to BGP loop prevention",
            Origin: "Well-known mandatory attribute; one of the original BGP path attributes",
            MED: "Optional non-transitive attribute; defined in RFC 4271 as MULTI_EXIT_DISC",
          },
        },
      ],
    },

    // -------------------------------------------------------------------------
    // cmp-005  STP Variants
    // -------------------------------------------------------------------------
    {
      id: "cmp-005",
      title: "STP Variants",
      domain: "3.0",
      protocols: ["PVST+", "Rapid PVST+", "MST"],
      is_custom: false,
      attributes: [
        {
          name: "IEEE standard",
          values: {
            "PVST+": "Cisco proprietary (based on 802.1D)",
            "Rapid PVST+": "Cisco proprietary (based on 802.1w)",
            MST: "IEEE 802.1s (incorporated into 802.1Q-2005)",
          },
          exam_hot: true,
          notes: {
            "PVST+": "PVST+ is Cisco's per-VLAN implementation of legacy 802.1D STP",
            "Rapid PVST+": "Rapid PVST+ is Cisco's per-VLAN implementation of 802.1w — combines rapid convergence with per-VLAN control",
            MST: "MST is the only IEEE standard option; it maps multiple VLANs to fewer STP instances for scalability",
          },
        },
        {
          name: "Port states",
          values: {
            "PVST+": "Blocking, Listening, Learning, Forwarding, Disabled",
            "Rapid PVST+": "Discarding, Learning, Forwarding",
            MST: "Discarding, Learning, Forwarding",
          },
          exam_hot: true,
          notes: {
            "PVST+": "Five port states in 802.1D — Blocking and Listening both drop user frames but differ in BPDU handling",
            "Rapid PVST+": "802.1w collapsed Blocking + Listening + Disabled into a single Discarding state",
            MST: "MST uses the same three states as Rapid PVST+ (Discarding, Learning, Forwarding)",
          },
        },
        {
          name: "Convergence time",
          values: {
            "PVST+": "~30–50 s (15 s forward delay × 2 + hello timers)",
            "Rapid PVST+": "< 1 s (proposal/agreement handshake)",
            MST: "< 1 s (802.1w mechanics per region)",
          },
          exam_hot: true,
          notes: {
            "PVST+": "Legacy STP convergence: 20s max age + 15s listening + 15s learning = up to 50s total",
            "Rapid PVST+": "Rapid convergence relies on point-to-point full-duplex links and the proposal/agreement mechanism",
            MST: "MST uses the same rapid convergence mechanism as 802.1w within each MST region",
          },
        },
        {
          name: "Instance model",
          values: {
            "PVST+": "One STP instance per VLAN",
            "Rapid PVST+": "One STP instance per VLAN",
            MST: "Multiple VLANs mapped to fewer instances (IST + MSTIs)",
          },
          exam_hot: true,
          notes: {
            "PVST+": "One instance per VLAN = high CPU/memory on switches with many VLANs (thousands of BPDUs)",
            "Rapid PVST+": "Same per-VLAN model as PVST+ — still resource-intensive with many VLANs",
            MST: "MST reduces CPU/memory by grouping VLANs into instances; instance 0 = IST (Internal Spanning Tree) is always present",
          },
        },
        {
          name: "BPDU handling",
          values: {
            "PVST+": "Cisco PVST BPDUs per VLAN on tagged ports; untagged on native VLAN",
            "Rapid PVST+": "Rapid BPDUs per VLAN; sent every hello interval regardless of topology",
            MST: "Single BPDU with MST records; backwards compatible with 802.1D",
          },
          exam_hot: false,
          notes: {
            "PVST+": "PVST+ sends separate BPDUs per VLAN using SNAP encapsulation on trunk ports",
            "Rapid PVST+": "Rapid PVST+ ports send BPDUs every hello (2s) even with no changes — expired BPDU = port failure",
            MST: "MST sends a single BPDU carrying M-records for all instances — much more efficient than per-VLAN BPDUs",
          },
        },
        {
          name: "Topology change handling",
          values: {
            "PVST+": "TCN BPDU propagated to root; MAC table flushed for forward-delay period",
            "Rapid PVST+": "TC flag floods instantly; MAC table flushed on all non-edge ports",
            MST: "TC propagation within region; IST carries TC outside region",
          },
          exam_hot: false,
          notes: {
            "PVST+": "TCN must travel hop-by-hop to root bridge; root then sets TC bit — slow process",
            "Rapid PVST+": "TC handling is much faster — the detecting switch immediately flushes MACs and floods the TC flag",
            MST: "TC events are contained within the MST region; IST propagates TC information between regions",
          },
        },
      ],
    },

    // -------------------------------------------------------------------------
    // cmp-006  FHRP Comparison
    // -------------------------------------------------------------------------
    {
      id: "cmp-006",
      title: "FHRP Comparison",
      domain: "3.0",
      protocols: ["HSRP v1", "HSRP v2", "VRRP", "GLBP"],
      is_custom: false,
      attributes: [
        {
          name: "Open standard?",
          values: {
            "HSRP v1": "No — Cisco proprietary",
            "HSRP v2": "No — Cisco proprietary",
            VRRP: "Yes — RFC 5798 (VRRPv3 = RFC 5798 with IPv6)",
            GLBP: "No — Cisco proprietary",
          },
          exam_hot: true,
          notes: {
            "HSRP v1": "HSRP was developed by Cisco and is proprietary — cannot be used with non-Cisco devices",
            "HSRP v2": "HSRP v2 is also Cisco proprietary; adds extended group range and IPv6 support",
            VRRP: "VRRP is the only open-standard FHRP — use it in multi-vendor environments",
            GLBP: "GLBP is Cisco proprietary — provides native load balancing but limited to Cisco-only networks",
          },
        },
        {
          name: "Virtual MAC format",
          values: {
            "HSRP v1": "0000.0c07.acXX (XX = group number in hex)",
            "HSRP v2": "0000.0c9f.fXXX (XXX = group number 0–4095 in hex)",
            VRRP: "0000.5e00.01XX (XX = VRID in hex)",
            GLBP: "0007.b400.XXYY (XX = group, YY = AVF index)",
          },
          exam_hot: true,
          notes: {
            "HSRP v1": "MAC prefix 0000.0c07.ac identifies HSRP v1 in packet captures; XX is group in hex",
            "HSRP v2": "Extended MAC format supports groups 0–4095; prefix 0000.0c9f.f identifies HSRP v2",
            VRRP: "MAC prefix 0000.5e00.01 is IANA-assigned for VRRP; XX is VRID in hex",
            GLBP: "GLBP uses multiple MACs per group — one per AVF — enabling true load balancing",
          },
        },
        {
          name: "Multicast address (control)",
          values: {
            "HSRP v1": "224.0.0.2 (UDP 1985)",
            "HSRP v2": "224.0.0.102 (UDP 1985)",
            VRRP: "224.0.0.18 (IP protocol 112)",
            GLBP: "224.0.0.102 (UDP 3222)",
          },
          exam_hot: true,
          notes: {
            "HSRP v1": "HSRP v1 uses 224.0.0.2 (all routers multicast) — can conflict with other protocols using the same address",
            "HSRP v2": "HSRP v2 moved to 224.0.0.102 to avoid conflicts with 224.0.0.2",
            VRRP: "VRRP uses 224.0.0.18 with IP protocol 112 — no UDP/TCP layer",
            GLBP: "GLBP shares 224.0.0.102 with HSRP v2 but uses a different UDP port (3222 vs 1985)",
          },
        },
        {
          name: "Hello / Hold timers (default)",
          values: {
            "HSRP v1": "Hello 3 s / Hold 10 s",
            "HSRP v2": "Hello 3 s / Hold 10 s",
            VRRP: "Advertisement interval 1 s; Master-down interval ~3.6 s",
            GLBP: "Hello 3 s / Hold 10 s",
          },
          exam_hot: false,
          notes: {
            "HSRP v1": "HSRP v1 supports millisecond timers for faster failover; hold time should be >= 3x hello",
            "HSRP v2": "Same default timers as v1; millisecond timers also supported",
            VRRP: "VRRP master-down interval = 3x advertisement + skew time; faster default detection than HSRP",
            GLBP: "Same default timers as HSRP; all four routers in a GLBP group use these timers",
          },
        },
        {
          name: "Preemption default",
          values: {
            "HSRP v1": "Disabled by default",
            "HSRP v2": "Disabled by default",
            VRRP: "Enabled by default",
            GLBP: "Disabled by default",
          },
          exam_hot: true,
          notes: {
            "HSRP v1": "HSRP requires 'standby preempt' to enable — without it, the original active keeps the role even if a higher-priority router comes online",
            "HSRP v2": "Same preemption behavior as v1 — must be explicitly enabled",
            VRRP: "VRRP preemption is ON by default — a higher-priority router will immediately take over as master",
            GLBP: "GLBP AVG preemption is disabled by default; AVF preemption is configured separately",
          },
        },
        {
          name: "Load balancing",
          values: {
            "HSRP v1": "No (active/standby only; multiple groups needed for LB)",
            "HSRP v2": "No (same as v1)",
            VRRP: "No (master/backup; multiple VRIDs for LB)",
            GLBP: "Yes — up to 4 AVFs per group; RR, weighted, or host-dependent",
          },
          exam_hot: true,
          notes: {
            "HSRP v1": "HSRP load balancing requires multiple groups with different active routers — hosts point to different VIPs",
            "HSRP v2": "Same limitation as v1; multiple groups with MHSRP (Multigroup HSRP) for load sharing",
            VRRP: "VRRP requires multiple VRIDs for load balancing — same workaround as HSRP",
            GLBP: "GLBP is the only FHRP with native load balancing using a single virtual IP — AVG assigns MACs to up to 4 AVFs",
          },
        },
        {
          name: "Group number range",
          values: {
            "HSRP v1": "0–255",
            "HSRP v2": "0–4095",
            VRRP: "1–255 (VRID)",
            GLBP: "0–1023",
          },
          exam_hot: false,
          notes: {
            "HSRP v1": "Limited to 256 groups (0–255); often sufficient for small deployments",
            "HSRP v2": "Extended to 4096 groups (0–4095) — aligns with the 4094 VLAN limit for per-VLAN HSRP",
            VRRP: "VRID starts at 1 (not 0); maximum 255 groups per interface",
            GLBP: "Up to 1024 groups (0–1023); each group supports up to 4 AVFs",
          },
        },
        {
          name: "IPv6 support",
          values: {
            "HSRP v1": "No",
            "HSRP v2": "Yes (HSRP for IPv6)",
            VRRP: "Yes — VRRPv3 (RFC 5798)",
            GLBP: "Yes (GLBP for IPv6)",
          },
          exam_hot: false,
          notes: {
            "HSRP v1": "HSRP v1 is IPv4-only — upgrade to v2 for IPv6 support",
            "HSRP v2": "HSRP v2 supports both IPv4 and IPv6; configured under the interface with 'standby version 2'",
            VRRP: "VRRPv3 supports both IPv4 and IPv6 in a single protocol version — the most versatile standard FHRP",
            GLBP: "GLBP supports IPv6 but remains Cisco proprietary",
          },
        },
      ],
    },

    // -------------------------------------------------------------------------
    // cmp-010  RADIUS vs TACACS+
    // -------------------------------------------------------------------------
    {
      id: "cmp-010",
      title: "RADIUS vs TACACS+",
      domain: "5.0",
      protocols: ["RADIUS", "TACACS+"],
      is_custom: false,
      attributes: [
        {
          name: "Transport protocol / port",
          values: {
            RADIUS: "UDP — Auth/Authz: 1812, Accounting: 1813 (legacy: 1645/1646)",
            "TACACS+": "TCP — port 49",
          },
          exam_hot: true,
          notes: {
            RADIUS: "RADIUS uses UDP — no built-in reliability; the application layer handles retransmissions",
            "TACACS+": "TACACS+ uses TCP port 49 for reliable delivery of AAA messages",
          },
        },
        {
          name: "Encryption scope",
          values: {
            RADIUS: "Only the password field is encrypted (using MD5 + shared secret)",
            "TACACS+": "Entire packet body is encrypted",
          },
          exam_hot: true,
          notes: {
            RADIUS: "RADIUS only encrypts the password — username, attributes, and accounting data are sent in cleartext",
            "TACACS+": "Full packet body encryption provides superior confidentiality; all AAA data is protected",
          },
        },
        {
          name: "AAA separation",
          values: {
            RADIUS: "Authentication and Authorization combined; Accounting separate",
            "TACACS+": "Authentication, Authorization, and Accounting fully separated",
          },
          exam_hot: true,
          notes: {
            RADIUS: "RADIUS combines authn and authz in a single Access-Accept — cannot authorize without authenticating",
            "TACACS+": "Full AAA separation allows granular per-command authorization without re-authentication",
          },
        },
        {
          name: "Command authorization (per-command)",
          values: {
            RADIUS: "Not natively supported (limited workarounds)",
            "TACACS+": "Yes — per-command authorization is a core feature",
          },
          exam_hot: true,
          notes: {
            RADIUS: "RADIUS was designed for network access, not device administration — per-command authz is not part of the protocol",
            "TACACS+": "Per-command authorization is why TACACS+ is preferred for device administration — each CLI command can be individually authorized",
          },
        },
        {
          name: "Accounting detail",
          values: {
            RADIUS: "Supports accounting; used heavily for network access (802.1X, VPN)",
            "TACACS+": "Supports accounting; records each command executed on network device",
          },
          exam_hot: false,
          notes: {
            RADIUS: "RADIUS accounting is widely used for billing and session tracking in ISP and 802.1X deployments",
            "TACACS+": "TACACS+ command accounting logs every CLI command entered — critical for audit trails on network devices",
          },
        },
        {
          name: "Cisco-proprietary?",
          values: {
            RADIUS: "No — RFC 2865/2866 (open standard)",
            "TACACS+": "Yes — Cisco proprietary (TACACS and XTACACS are older; TACACS+ is Cisco)",
          },
          exam_hot: true,
          notes: {
            RADIUS: "RADIUS is the open standard defined in RFC 2865 (auth) and RFC 2866 (accounting) — works with any vendor",
            "TACACS+": "TACACS+ is Cisco-proprietary despite the similar naming to older TACACS/XTACACS protocols",
          },
        },
        {
          name: "Primary use case",
          values: {
            RADIUS: "Network access control: 802.1X, VPN authentication",
            "TACACS+": "Device administration: CLI access, command authorization on routers/switches",
          },
          exam_hot: true,
          notes: {
            RADIUS: "RADIUS is the standard for network access control — 802.1X, VPN, and Wi-Fi authentication all use RADIUS",
            "TACACS+": "TACACS+ is preferred for device administration on Cisco gear; Cisco ISE supports both protocols",
          },
        },
      ],
    },

    // -------------------------------------------------------------------------
    // cmp-011  SNMP Versions
    // -------------------------------------------------------------------------
    {
      id: "cmp-011",
      title: "SNMP v1 vs v2c vs v3",
      domain: "4.0",
      protocols: ["SNMPv1", "SNMPv2c", "SNMPv3"],
      is_custom: false,
      attributes: [
        {
          name: "Security model",
          values: {
            SNMPv1: "Community-based (cleartext community string)",
            SNMPv2c: "Community-based (cleartext; 'c' = community)",
            SNMPv3: "User-based Security Model (USM) with users and groups",
          },
          exam_hot: true,
          notes: {
            SNMPv1: "Community strings in v1 are sent in cleartext — essentially a plaintext password on the wire",
            SNMPv2c: "The 'c' in v2c stands for community — same cleartext security as v1 despite protocol improvements",
            SNMPv3: "USM replaces community strings with usernames, authentication, and encryption — the only secure SNMP version",
          },
        },
        {
          name: "Authentication",
          values: {
            SNMPv1: "None (community string only)",
            SNMPv2c: "None (community string only)",
            SNMPv3: "Optional — HMAC-MD5 or HMAC-SHA (authNoPriv/authPriv levels)",
          },
          exam_hot: true,
          notes: {
            SNMPv1: "No real authentication — the community string is not a security mechanism",
            SNMPv2c: "Same lack of authentication as v1 — community strings provide no real identity verification",
            SNMPv3: "Three security levels: noAuthNoPriv (username only), authNoPriv (HMAC auth), authPriv (auth + encryption)",
          },
        },
        {
          name: "Encryption",
          values: {
            SNMPv1: "None",
            SNMPv2c: "None",
            SNMPv3: "Optional — DES, 3DES, AES-128/192/256 (priv level)",
          },
          exam_hot: true,
          notes: {
            SNMPv1: "All SNMP data transmitted in cleartext — vulnerable to sniffing",
            SNMPv2c: "Still no encryption despite being a newer version — cleartext community strings remain",
            SNMPv3: "Only SNMPv3 with authPriv provides encryption; AES-128 or higher is recommended over deprecated DES",
          },
        },
        {
          name: "Community strings",
          values: {
            SNMPv1: "Yes — required for all operations",
            SNMPv2c: "Yes — required for all operations",
            SNMPv3: "No — replaced by username/password and engineID",
          },
          exam_hot: true,
          notes: {
            SNMPv1: "Default community strings 'public' (RO) and 'private' (RW) should always be changed",
            SNMPv2c: "Same community string model as v1; 'snmp-server community <string> RO/RW' to configure",
            SNMPv3: "Replaces communities with engineID + user credentials; 'snmp-server user' command to configure",
          },
        },
        {
          name: "GetBulk operation",
          values: {
            SNMPv1: "No — only GetNext",
            SNMPv2c: "Yes — GetBulk for efficient table retrieval",
            SNMPv3: "Yes — GetBulk supported",
          },
          exam_hot: false,
          notes: {
            SNMPv1: "v1 must use repeated GetNext calls to walk a MIB table — slow and inefficient",
            SNMPv2c: "GetBulk was a major improvement in v2c — retrieves multiple OIDs in a single request",
            SNMPv3: "GetBulk is supported with the same efficiency as v2c, plus security",
          },
        },
        {
          name: "Access control",
          values: {
            SNMPv1: "Community string = RO or RW access",
            SNMPv2c: "Community string = RO or RW access",
            SNMPv3: "VACM (View-based Access Control Model) — granular OID-level access",
          },
          exam_hot: false,
          notes: {
            SNMPv1: "Access control is binary — either read-only or read-write based on community string",
            SNMPv2c: "Same coarse RO/RW access model as v1; optional view filtering with 'snmp-server view'",
            SNMPv3: "VACM allows restricting specific users/groups to specific MIB subtrees — much finer granularity",
          },
        },
      ],
    },

    // -------------------------------------------------------------------------
    // cmp-012  NetFlow vs sFlow vs IPFIX vs Streaming Telemetry
    // -------------------------------------------------------------------------
    {
      id: "cmp-012",
      title: "NetFlow vs sFlow vs IPFIX vs Streaming Telemetry",
      domain: "4.0",
      protocols: ["NetFlow v9", "sFlow", "IPFIX", "Streaming Telemetry"],
      is_custom: false,
      attributes: [
        {
          name: "Sampling method",
          values: {
            "NetFlow v9": "Flow-based (cache); optional sampling",
            sFlow: "Packet sampling (1 in N packets) + counter sampling",
            IPFIX: "Flow-based (like NetFlow); optional sampling per RFC 5477",
            "Streaming Telemetry": "Push-based subscription; no sampling — event or periodic",
          },
          exam_hot: true,
          notes: {
            "NetFlow v9": "NetFlow tracks complete flows in a cache — every packet in a flow is accounted for unless sampling is enabled",
            sFlow: "sFlow uses random 1-in-N packet sampling — lightweight but statistical; accuracy improves with more traffic",
            IPFIX: "IPFIX inherits NetFlow v9's flow-based approach with standardized template definitions",
            "Streaming Telemetry": "Telemetry does not sample traffic — it streams operational data (counters, state) directly from the device",
          },
        },
        {
          name: "Export / data format",
          values: {
            "NetFlow v9": "Cisco proprietary template-based format",
            sFlow: "sFlow datagram (open standard — RFC 3176)",
            IPFIX: "IETF standard (RFC 7011); based on NetFlow v9 templates",
            "Streaming Telemetry": "gRPC + protobuf or JSON over RESTCONF/NETCONF subscriptions",
          },
          exam_hot: true,
          notes: {
            "NetFlow v9": "NetFlow v9 introduced templates that made the format extensible; it was the basis for IPFIX",
            sFlow: "sFlow datagrams are self-describing and vendor-neutral — widely supported by switch ASICs",
            IPFIX: "IPFIX is the IETF standardization of NetFlow v9 — often called 'NetFlow v10' informally",
            "Streaming Telemetry": "gRPC + protobuf is the most efficient encoding; JSON via RESTCONF is more human-readable",
          },
        },
        {
          name: "Transport",
          values: {
            "NetFlow v9": "UDP (typically port 2055 or 9995/9996)",
            sFlow: "UDP (typically port 6343)",
            IPFIX: "UDP or SCTP or TCP (RFC 7011)",
            "Streaming Telemetry": "gRPC (TCP); HTTPS for RESTCONF-based subscriptions",
          },
          exam_hot: false,
          notes: {
            "NetFlow v9": "UDP transport means flow records can be lost — no reliability guarantee",
            sFlow: "UDP 6343 is the standard sFlow collector port; same unreliable transport as NetFlow",
            IPFIX: "IPFIX supports SCTP and TCP for reliable transport — an improvement over NetFlow's UDP-only",
            "Streaming Telemetry": "gRPC over TCP provides reliable, bidirectional streaming — the most robust transport option",
          },
        },
        {
          name: "Push or Pull model",
          values: {
            "NetFlow v9": "Push — device exports flows to collector",
            sFlow: "Push — agent exports samples to collector",
            IPFIX: "Push — exporter sends to IPFIX collector",
            "Streaming Telemetry": "Push — device streams data to subscriber after subscription",
          },
          exam_hot: true,
          notes: {
            "NetFlow v9": "NetFlow pushes cached flow records to the collector at export intervals or on flow expiry",
            sFlow: "sFlow agent pushes sampled packets and counters to the collector continuously",
            IPFIX: "IPFIX follows the same push model as NetFlow — exporter initiates data transfer to collector",
            "Streaming Telemetry": "Telemetry supports dial-out (device pushes) and dial-in (collector subscribes) — both are push after setup",
          },
        },
        {
          name: "Standards body",
          values: {
            "NetFlow v9": "Cisco proprietary (RFC 3954 informational)",
            sFlow: "sFlow.org consortium; RFC 3176",
            IPFIX: "IETF — RFC 7011, 7012, 7013 (standards track)",
            "Streaming Telemetry": "OpenConfig / IETF (gRPC, YANG models)",
          },
          exam_hot: false,
          notes: {
            "NetFlow v9": "NetFlow v9 is documented in RFC 3954 but as informational only — it remains Cisco proprietary",
            sFlow: "sFlow is maintained by sFlow.org and documented in RFC 3176; widely supported by non-Cisco vendors",
            IPFIX: "IPFIX is the IETF standards-track replacement for proprietary flow protocols like NetFlow",
            "Streaming Telemetry": "OpenConfig defines gNMI and YANG models; IETF defines YANG Push — both are used in practice",
          },
        },
        {
          name: "Granularity / use case",
          values: {
            "NetFlow v9": "Per-flow statistics; traffic engineering, billing, security",
            sFlow: "High-speed links; lightweight; statistical accuracy",
            IPFIX: "Same as NetFlow v9 but interoperable and extensible",
            "Streaming Telemetry": "Real-time operational data (interface counters, routing state, health)",
          },
          exam_hot: true,
          notes: {
            "NetFlow v9": "NetFlow is ideal for traffic analysis, capacity planning, and security anomaly detection",
            sFlow: "sFlow excels on high-speed links (40G/100G) where full flow tracking would be too resource-intensive",
            IPFIX: "IPFIX adds extensibility via enterprise-specific information elements — future-proof flow monitoring",
            "Streaming Telemetry": "Telemetry is superior for real-time, model-driven monitoring — the future of network observability",
          },
        },
      ],
    },

    // -------------------------------------------------------------------------
    // cmp-013  NETCONF vs RESTCONF
    // -------------------------------------------------------------------------
    {
      id: "cmp-013",
      title: "NETCONF vs RESTCONF",
      domain: "4.0",
      protocols: ["NETCONF", "RESTCONF"],
      is_custom: false,
      attributes: [
        {
          name: "Transport",
          values: {
            NETCONF: "SSH (mandatory) — RFC 6242; also SOAP/HTTP (RFC 4743, deprecated)",
            RESTCONF: "HTTPS (TLS) — RFC 8040",
          },
          exam_hot: true,
          notes: {
            NETCONF: "NETCONF over SSH uses TCP port 830 as the registered subsystem port",
            RESTCONF: "RESTCONF runs over HTTPS (port 443) — requires 'ip http secure-server' on IOS-XE",
          },
        },
        {
          name: "Default port",
          values: {
            NETCONF: "830 (SSH subsystem)",
            RESTCONF: "443 (HTTPS)",
          },
          exam_hot: true,
          notes: {
            NETCONF: "Port 830 is the IANA-registered port for NETCONF over SSH — frequently tested",
            RESTCONF: "Port 443 is standard HTTPS — RESTCONF shares the port with other HTTPS services on the device",
          },
        },
        {
          name: "Data encoding format",
          values: {
            NETCONF: "XML only",
            RESTCONF: "XML or JSON (JSON more common in practice)",
          },
          exam_hot: true,
          notes: {
            NETCONF: "NETCONF is XML-only — all RPC requests and replies are XML-encoded",
            RESTCONF: "RESTCONF's JSON support makes it more developer-friendly and easier to integrate with modern tools",
          },
        },
        {
          name: "Operations / Methods",
          values: {
            NETCONF: "get, get-config, edit-config, copy-config, delete-config, lock, unlock, close-session, kill-session",
            RESTCONF: "HTTP methods: GET, POST, PUT, PATCH, DELETE, OPTIONS",
          },
          exam_hot: true,
          notes: {
            NETCONF: "NETCONF operations are RPC-based; edit-config is the primary configuration change operation",
            RESTCONF: "RESTCONF maps HTTP verbs to CRUD: GET=read, POST=create, PUT=replace, PATCH=update, DELETE=remove",
          },
        },
        {
          name: "Data modeling language",
          values: {
            NETCONF: "YANG (RFC 6020 / RFC 7950)",
            RESTCONF: "YANG (RFC 6020 / RFC 7950) — same models, different transport",
          },
          exam_hot: true,
          notes: {
            NETCONF: "YANG is the native modeling language for NETCONF — the protocol was designed around YANG data models",
            RESTCONF: "RESTCONF uses the same YANG models as NETCONF — the URL path structure maps directly to YANG schema nodes",
          },
        },
        {
          name: "Connection type",
          values: {
            NETCONF: "Stateful, persistent TCP session (SSH)",
            RESTCONF: "Stateless HTTP requests (each request independent)",
          },
          exam_hot: true,
          notes: {
            NETCONF: "NETCONF's stateful session enables locks, candidate datastore edits, and multi-step transactions",
            RESTCONF: "RESTCONF is stateless like REST — each request is self-contained; no session locks available",
          },
        },
        {
          name: "Transaction / rollback support",
          values: {
            NETCONF: "Yes — candidate datastore, confirmed-commit, rollback-on-error",
            RESTCONF: "Limited — no candidate datastore; relies on atomic PUT/PATCH per RFC 8040",
          },
          exam_hot: false,
          notes: {
            NETCONF: "NETCONF datastores (running, startup, candidate) enable safe config staging and rollback on error",
            RESTCONF: "RESTCONF lacks candidate datastore — changes go directly to running config; less transactional safety",
          },
        },
        {
          name: "RFC",
          values: {
            NETCONF: "RFC 6241 (NETCONF 1.1); RFC 6242 (NETCONF over SSH)",
            RESTCONF: "RFC 8040",
          },
          exam_hot: false,
          notes: {
            NETCONF: "RFC 6241 defines the protocol; RFC 6242 defines SSH transport; RFC 7950 defines YANG 1.1",
            RESTCONF: "RFC 8040 is the single defining standard for RESTCONF — much simpler RFC landscape than NETCONF",
          },
        },
      ],
    },

    // -------------------------------------------------------------------------
    // cmp-014  GRE vs IPsec vs DMVPN vs VXLAN
    // -------------------------------------------------------------------------
    {
      id: "cmp-014",
      title: "GRE vs IPsec vs DMVPN vs VXLAN",
      domain: "2.0",
      protocols: ["GRE", "IPsec", "DMVPN", "VXLAN"],
      is_custom: false,
      attributes: [
        {
          name: "Encapsulation overhead",
          values: {
            GRE: "24 bytes (20 IP + 4 GRE header; more with keys/checksum)",
            IPsec: "~50–60 bytes (ESP tunnel mode: 20 IP + 8 ESP + 12–20 auth)",
            DMVPN: "GRE + IPsec: ~58+ bytes (GRE + mGRE over IPsec)",
            VXLAN: "50 bytes (14 ETH + 20 IP + 8 UDP + 8 VXLAN header)",
          },
          exam_hot: false,
          notes: {
            GRE: "GRE adds 24 bytes minimum; optional key and checksum fields add 4 bytes each",
            IPsec: "ESP tunnel mode has the highest overhead; plan for ~1400 byte MSS with IPsec",
            DMVPN: "DMVPN combines GRE + IPsec overhead; MSS clamping to ~1400 bytes is critical to avoid fragmentation",
            VXLAN: "VXLAN adds 50 bytes; underlay MTU should be at least 1550 bytes to avoid fragmentation",
          },
        },
        {
          name: "Encryption",
          values: {
            GRE: "None natively (plaintext tunnel)",
            IPsec: "Yes — AES, 3DES; IKEv1 or IKEv2 for key exchange",
            DMVPN: "Yes — IPsec profile applied to mGRE interface (Phase 1/2/3)",
            VXLAN: "None natively; VXLAN+GPE or MACsec used for encryption",
          },
          exam_hot: true,
          notes: {
            GRE: "GRE provides zero security — it is a tunneling protocol only; pair with IPsec for encryption",
            IPsec: "IPsec provides confidentiality (ESP encryption), integrity (HMAC), and authentication (IKE); IKEv2 is preferred",
            DMVPN: "DMVPN = mGRE + NHRP + IPsec — the IPsec profile provides encryption for all tunnel traffic",
            VXLAN: "VXLAN has no built-in encryption; use MACsec (802.1AE) on the underlay or CloudSec for encryption",
          },
        },
        {
          name: "Multicast / routing protocol support",
          values: {
            GRE: "Yes — passes multicast; supports all routing protocols",
            IPsec: "No multicast natively (tunnel mode; workarounds exist)",
            DMVPN: "Yes — mGRE passes multicast for routing protocol adjacencies",
            VXLAN: "Yes — unicast (flood-and-learn) or multicast underlay; or BGP EVPN",
          },
          exam_hot: true,
          notes: {
            GRE: "GRE's ability to encapsulate multicast is why it is paired with IPsec — IPsec alone cannot carry multicast",
            IPsec: "IPsec tunnel mode does not support multicast natively; GRE-over-IPsec or VTI with DVMRP are workarounds",
            DMVPN: "DMVPN's mGRE enables multicast, allowing OSPF/EIGRP adjacencies over the overlay — key advantage over pure IPsec",
            VXLAN: "VXLAN can use multicast groups for BUM traffic flooding or BGP EVPN for control-plane-based learning",
          },
        },
        {
          name: "Scalability",
          values: {
            GRE: "Low — point-to-point; each pair needs a separate tunnel",
            IPsec: "Moderate — crypto map or VTI; many tunnels possible but complex",
            DMVPN: "High — single mGRE interface; dynamic spoke-to-spoke; scales to thousands",
            VXLAN: "Very high — 16 million segment IDs (24-bit VNI); data center scale",
          },
          exam_hot: true,
          notes: {
            GRE: "GRE requires N*(N-1)/2 tunnels for full mesh — impractical at scale",
            IPsec: "VTI (Virtual Tunnel Interface) simplifies IPsec but still requires per-peer configuration",
            DMVPN: "Single mGRE interface handles all spokes; NHRP enables dynamic spoke-to-spoke tunnels without hub involvement",
            VXLAN: "24-bit VNI provides 16M+ segments vs 4096 VLAN limit — essential for multi-tenant data centers",
          },
        },
        {
          name: "Primary use case",
          values: {
            GRE: "Simple tunneling for non-IP protocols, routing protocol adjacencies over WAN",
            IPsec: "Secure site-to-site VPN; remote access VPN (IKEv2/EAP)",
            DMVPN: "Hub-and-spoke WAN with dynamic spoke-to-spoke; SD-WAN predecessor",
            VXLAN: "Data center overlay; Layer 2 extension over Layer 3; tenant isolation",
          },
          exam_hot: true,
          notes: {
            GRE: "GRE is commonly used to carry routing protocols or multicast over an IPsec tunnel",
            IPsec: "IPsec is the standard for secure site-to-site and remote access VPNs in enterprise networks",
            DMVPN: "DMVPN was the dominant WAN overlay technology before SD-WAN; still widely deployed",
            VXLAN: "VXLAN is the standard data center overlay fabric — used in Cisco ACI and EVPN-VXLAN deployments",
          },
        },
        {
          name: "Protocol / Port",
          values: {
            GRE: "IP protocol 47",
            IPsec: "IKE: UDP 500; NAT-T: UDP 4500; ESP: IP protocol 50; AH: IP protocol 51",
            DMVPN: "GRE (IP 47) + IPsec (UDP 500/4500, IP 50); NHRP uses GRE",
            VXLAN: "UDP port 4789 (IANA assigned)",
          },
          exam_hot: true,
          notes: {
            GRE: "GRE is IP protocol 47 — not a TCP/UDP port; this matters for firewall ACLs",
            IPsec: "IKE uses UDP 500; NAT traversal uses UDP 4500; ESP is IP protocol 50 — know all three for ACL questions",
            DMVPN: "NHRP is carried inside GRE (protocol type 0x2001); no separate port needed for NHRP",
            VXLAN: "VXLAN uses UDP destination port 4789 (IANA); source port is a hash for ECMP load balancing",
          },
        },
      ],
    },

    // -------------------------------------------------------------------------
    // cmp-015  DMVPN Phases 1 / 2 / 3
    // -------------------------------------------------------------------------
    {
      id: "cmp-015",
      title: "DMVPN Phases 1 vs 2 vs 3",
      domain: "2.0",
      protocols: ["Phase 1", "Phase 2", "Phase 3"],
      is_custom: false,
      attributes: [
        {
          name: "Spoke-to-spoke direct tunnels",
          values: {
            "Phase 1": "No — all traffic goes through hub (hub-and-spoke only)",
            "Phase 2": "Yes — dynamic spoke-to-spoke tunnels via NHRP resolution",
            "Phase 3": "Yes — dynamic spoke-to-spoke tunnels; more scalable than Phase 2",
          },
          exam_hot: true,
          notes: {
            "Phase 1": "Phase 1 is pure hub-and-spoke — all traffic transits the hub router, creating a bottleneck",
            "Phase 2": "Phase 2 enables direct spoke-to-spoke tunnels via NHRP resolution — reduces hub load",
            "Phase 3": "Phase 3 also enables spoke-to-spoke but uses NHRP Redirect for better scalability than Phase 2",
          },
        },
        {
          name: "NHRP behavior",
          values: {
            "Phase 1": "Spokes register with hub (NHS); hub answers NHRP requests",
            "Phase 2": "Spokes send NHRP Resolution Request to hub; hub proxies to target spoke; spoke caches NBMA address",
            "Phase 3": "Hub sends NHRP Redirect to initiating spoke; spoke queries target directly via NHRP; hub triggers redirect",
          },
          exam_hot: true,
          notes: {
            "Phase 1": "Spokes register their NBMA-to-tunnel IP mapping with the NHS (hub); hub is the only NHRP responder",
            "Phase 2": "Hub acts as NHRP proxy — it resolves the target spoke's NBMA address on behalf of the requesting spoke",
            "Phase 3": "NHRP Redirect (from hub) + NHRP Resolution Request (spoke-to-spoke directly) is more efficient and scalable",
          },
        },
        {
          name: "Routing requirements",
          values: {
            "Phase 1": "Hub advertises spoke networks; spokes have default or specific routes via hub; no restrictions",
            "Phase 2": "Routing protocol must not summarize at hub (spokes need specific spoke-subnet routes to trigger NHRP)",
            "Phase 3": "Hub CAN summarize (default route sufficient); NHRP redirect handles specific path",
          },
          exam_hot: true,
          notes: {
            "Phase 1": "No routing restrictions — hub can summarize freely since all traffic goes through it anyway",
            "Phase 2": "Spokes need specific routes to trigger NHRP resolution — summarization at the hub breaks spoke-to-spoke",
            "Phase 3": "Hub can advertise a summary or default route; NHRP redirect overrides the routing table with a specific next-hop",
          },
        },
        {
          name: "Summarization at hub",
          values: {
            "Phase 1": "Allowed — no spoke-to-spoke routing needed",
            "Phase 2": "Not allowed — spokes need specific prefixes to initiate NHRP",
            "Phase 3": "Allowed — hub can advertise a summary/default; NHRP redirect provides specific next-hop",
          },
          exam_hot: true,
          notes: {
            "Phase 1": "Summarization is fine in Phase 1 because all traffic transits the hub regardless",
            "Phase 2": "No-summarization requirement is Phase 2's biggest scalability limitation — each spoke needs every other spoke's prefix",
            "Phase 3": "Phase 3 solves Phase 2's summarization limitation — this is the key exam differentiator between Phase 2 and 3",
          },
        },
        {
          name: "Hub in data plane (steady state)",
          values: {
            "Phase 1": "Yes — all spoke traffic transits hub",
            "Phase 2": "No — after NHRP resolution, spoke-to-spoke direct (hub bypassed)",
            "Phase 3": "No — after NHRP redirect, spoke-to-spoke direct (hub bypassed)",
          },
          exam_hot: true,
          notes: {
            "Phase 1": "Hub is always in the data path — creates bandwidth bottleneck and single point of failure for data plane",
            "Phase 2": "After NHRP resolution completes, traffic flows directly spoke-to-spoke; hub is removed from the data path",
            "Phase 3": "Same as Phase 2 — hub exits the data plane after NHRP redirect; only the initial packets transit the hub",
          },
        },
        {
          name: "Recommended use",
          values: {
            "Phase 1": "Simple hub-and-spoke; no spoke-to-spoke needed; easy to configure",
            "Phase 2": "Spoke-to-spoke needed; small deployments; no summarization at hub",
            "Phase 3": "Large scale; spoke-to-spoke needed; summarization desired; EIGRP or OSPF with NHRP shortcuts",
          },
          exam_hot: false,
          notes: {
            "Phase 1": "Phase 1 is the simplest DMVPN deployment; appropriate when all traffic naturally flows through the hub",
            "Phase 2": "Phase 2 works for small-to-medium deployments where maintaining full routing tables at spokes is acceptable",
            "Phase 3": "Phase 3 is Cisco's recommended approach for scalable DMVPN — supports summarization with spoke-to-spoke shortcuts",
          },
        },
      ],
    },

    // -------------------------------------------------------------------------
    // cmp-016  SD-WAN Components
    // -------------------------------------------------------------------------
    {
      id: "cmp-016",
      title: "Cisco SD-WAN Components",
      domain: "1.0",
      protocols: ["vManage", "vSmart", "vBond", "vEdge/cEdge"],
      is_custom: false,
      attributes: [
        {
          name: "Plane",
          values: {
            vManage: "Management plane",
            vSmart: "Control plane",
            vBond: "Orchestration plane",
            "vEdge/cEdge": "Data plane",
          },
          exam_hot: true,
          notes: {
            vManage: "vManage = management plane — provides the centralized GUI/API for configuration and monitoring",
            vSmart: "vSmart = control plane — distributes routing policy and crypto keys via OMP to all WAN edges",
            vBond: "vBond = orchestration plane — the first contact point for new devices joining the SD-WAN fabric",
            "vEdge/cEdge": "vEdge/cEdge = data plane — the actual WAN routers forwarding user traffic through IPsec tunnels",
          },
        },
        {
          name: "Primary function",
          values: {
            vManage: "Centralized GUI and REST API for configuration, monitoring, and policy management",
            vSmart: "Distributes routing and policy information to vEdge/cEdge devices via OMP",
            vBond: "Initial authenticator; facilitates NAT traversal and helps devices discover vManage/vSmart",
            "vEdge/cEdge": "Physical or virtual router forwarding data traffic; enforces policies from vSmart",
          },
          exam_hot: true,
          notes: {
            vManage: "vManage pushes templates and policies; it also provides real-time monitoring dashboards",
            vSmart: "vSmart uses OMP to distribute routes, service chains, and crypto keys — it does NOT forward data traffic",
            vBond: "vBond must have a public IP or be reachable from all WAN edges — it orchestrates initial device onboarding",
            "vEdge/cEdge": "cEdge runs IOS-XE with SD-WAN personality; vEdge runs Viptela OS — both enforce vSmart policies",
          },
        },
        {
          name: "Control protocol",
          values: {
            vManage: "HTTPS/REST API; NETCONF for device config push",
            vSmart: "OMP (Overlay Management Protocol) to WAN edges; DTLS/TLS control connections",
            vBond: "DTLS; orchestrates initial connection to vManage and vSmart",
            "vEdge/cEdge": "OMP to vSmart; IPsec BFD for data plane tunnels between edges",
          },
          exam_hot: true,
          notes: {
            vManage: "vManage uses NETCONF to push device-level configurations to cEdge routers",
            vSmart: "OMP is Cisco SD-WAN's proprietary routing protocol — it carries routes, TLOC info, policies, and crypto keys",
            vBond: "vBond uses DTLS/TLS for the initial control connection; it redirects devices to vManage and vSmart",
            "vEdge/cEdge": "Data plane tunnels between edges use IPsec with BFD for liveness detection and SLA monitoring",
          },
        },
        {
          name: "Redundancy",
          values: {
            vManage: "Cluster of up to 6 nodes; NMS cluster for HA",
            vSmart: "Multiple vSmart controllers (recommended 2+); each edge connects to 2 vSmarts",
            vBond: "Multiple vBond instances supported; DNS-based load balancing",
            "vEdge/cEdge": "Redundant WAN links; active/active multi-path across transports",
          },
          exam_hot: false,
          notes: {
            vManage: "vManage cluster provides HA for the management plane; minimum 3 nodes for a cluster",
            vSmart: "Loss of vSmart doesn't break existing tunnels but prevents new policy/route updates — HA is critical",
            vBond: "Multiple vBond instances with DNS round-robin provide orchestration plane redundancy",
            "vEdge/cEdge": "WAN edges support active/active across multiple transports (MPLS, Internet, LTE) simultaneously",
          },
        },
        {
          name: "Device types",
          values: {
            vManage: "VM (ESXi, KVM) or Cisco-hosted cloud",
            vSmart: "VM (ESXi, KVM) or Cisco-hosted cloud",
            vBond: "VM or physical (runs on vEdge platform)",
            "vEdge/cEdge": "vEdge: Viptela hardware/VM; cEdge: Cisco IOS-XE routers (ISR/ASR/CSR)",
          },
          exam_hot: false,
          notes: {
            vManage: "vManage can run on-premises as a VM or as Cisco-hosted cloud — cloud is the newer deployment model",
            vSmart: "vSmart is always a VM or cloud instance — there is no physical vSmart appliance",
            vBond: "vBond can run on physical vEdge hardware or as a VM — it needs a public-facing IP for device onboarding",
            "vEdge/cEdge": "Cisco acquired Viptela in 2017; cEdge (IOS-XE) is the strategic platform replacing vEdge (Viptela OS)",
          },
        },
      ],
    },

    // -------------------------------------------------------------------------
    // cmp-017  DNA Center vs vManage vs Meraki
    // -------------------------------------------------------------------------
    {
      id: "cmp-017",
      title: "DNA Center vs vManage vs Meraki Dashboard",
      domain: "1.0",
      protocols: ["DNA Center (Catalyst Center)", "vManage (SD-WAN)", "Meraki Dashboard"],
      is_custom: false,
      attributes: [
        {
          name: "Scope / domain",
          values: {
            "DNA Center (Catalyst Center)": "Campus and branch LAN (SD-Access); some WAN integration",
            "vManage (SD-WAN)": "WAN overlay management (SD-WAN fabric)",
            "Meraki Dashboard": "Full-stack cloud management: switching, security, SD-WAN (MX)",
          },
          exam_hot: true,
          notes: {
            "DNA Center (Catalyst Center)": "DNA Center was rebranded to Catalyst Center in 2023; ENCOR still references both names — know both",
            "vManage (SD-WAN)": "vManage is exclusively for SD-WAN fabric management — it does not manage campus LAN switches",
            "Meraki Dashboard": "Meraki manages the full stack (switch, firewall, AP, camera) from one cloud dashboard — but only Meraki hardware",
          },
        },
        {
          name: "Target network technology",
          values: {
            "DNA Center (Catalyst Center)": "Cisco Catalyst switches, APs, ISE; SDA with LISP+VXLAN+SGT",
            "vManage (SD-WAN)": "Cisco SD-WAN fabric (vEdge, cEdge — ISR/ASR/CSR with IOS-XE SD-WAN)",
            "Meraki Dashboard": "Meraki hardware only (MX, MS, MR, MV, MT product lines)",
          },
          exam_hot: true,
          notes: {
            "DNA Center (Catalyst Center)": "DNA Center uses SDA (SD-Access) with LISP for control plane, VXLAN for data plane, and SGTs for segmentation",
            "vManage (SD-WAN)": "vManage manages cEdge (IOS-XE) and vEdge (Viptela OS) routers running SD-WAN software",
            "Meraki Dashboard": "Meraki is a separate product line — cannot manage Catalyst or ISR/ASR devices",
          },
        },
        {
          name: "API model",
          values: {
            "DNA Center (Catalyst Center)": "REST API (Intent API, Integration API, Multivendor SDK); YANG/NETCONF for devices",
            "vManage (SD-WAN)": "REST API (vManage REST API); NETCONF to vEdge/cEdge",
            "Meraki Dashboard": "REST API (Meraki Dashboard API); webhook event-driven",
          },
          exam_hot: true,
          notes: {
            "DNA Center (Catalyst Center)": "DNA Center Intent API abstracts complex workflows into simple API calls — a key ENCOR automation topic",
            "vManage (SD-WAN)": "vManage REST API enables programmable policy deployment and monitoring for SD-WAN devices",
            "Meraki Dashboard": "Meraki API is the simplest of the three — pure REST with API key authentication; supports webhooks for events",
          },
        },
        {
          name: "Deployment model",
          values: {
            "DNA Center (Catalyst Center)": "On-premises appliance (1/2/3-node cluster) or cloud (Catalyst Center Cloud)",
            "vManage (SD-WAN)": "On-premises VM, cloud-hosted (Cisco-managed), or customer-managed cloud",
            "Meraki Dashboard": "100% cloud-hosted SaaS (no on-premises option)",
          },
          exam_hot: true,
          notes: {
            "DNA Center (Catalyst Center)": "Catalyst Center was traditionally on-prem first; cloud option is newer and expanding",
            "vManage (SD-WAN)": "vManage supports flexible deployment — on-prem VM, Cisco cloud, or customer-managed cloud (AWS/Azure)",
            "Meraki Dashboard": "Meraki is always 100% cloud — no on-premises management option; requires internet connectivity",
          },
        },
        {
          name: "Licensing model",
          values: {
            "DNA Center (Catalyst Center)": "Cisco DNA / Catalyst Advantage/Essentials subscriptions per device",
            "vManage (SD-WAN)": "Cisco SD-WAN licenses (DNA-WAN tiers: Essentials, Advantage, Premier)",
            "Meraki Dashboard": "Annual license per device required; dashboard access tied to active license",
          },
          exam_hot: false,
          notes: {
            "DNA Center (Catalyst Center)": "DNA Advantage includes ISE integration and advanced automation features; Essentials is more limited",
            "vManage (SD-WAN)": "SD-WAN licensing tiers (Essentials/Advantage/Premier) determine available features like application optimization",
            "Meraki Dashboard": "Meraki devices become unmanageable if the license expires — the hardware requires an active subscription",
          },
        },
        {
          name: "Assurance / analytics capability",
          values: {
            "DNA Center (Catalyst Center)": "Yes — AI-driven Network Assurance; client 360, device 360, issues, proactive insights",
            "vManage (SD-WAN)": "Yes — vAnalytics (cloud-based); application performance, SLA monitoring",
            "Meraki Dashboard": "Yes — built-in cloud analytics; application visibility (AVC)",
          },
          exam_hot: false,
          notes: {
            "DNA Center (Catalyst Center)": "DNA Center Assurance uses streaming telemetry and ML for root cause analysis — a key ENCOR topic",
            "vManage (SD-WAN)": "vAnalytics provides cloud-based analytics for application performance and WAN SLA compliance",
            "Meraki Dashboard": "Meraki provides built-in application visibility and client analytics without additional licensing",
          },
        },
      ],
    },

    // -------------------------------------------------------------------------
    // cmp-018  EIGRP Classic vs Named Mode
    // -------------------------------------------------------------------------
    {
      id: "cmp-018",
      title: "EIGRP Classic vs Named Mode",
      domain: "3.0",
      protocols: ["Classic Mode", "Named Mode"],
      is_custom: false,
      attributes: [
        {
          name: "Configuration scope",
          values: {
            "Classic Mode": "Interface-level and router-level commands are split across both contexts",
            "Named Mode": "All configuration consolidated under 'router eigrp <name>' with address-family and af-interface sub-modes",
          },
          exam_hot: false,
          notes: {
            "Classic Mode": "Classic mode scatters EIGRP config between 'router eigrp' and individual interface commands — harder to audit",
            "Named Mode": "Named mode consolidates everything under one hierarchy — introduced in IOS-XE 3.2 / IOS 15.0(1)M; Cisco recommends it for all new deployments",
          },
        },
        {
          name: "Authentication options",
          values: {
            "Classic Mode": "MD5 only (key chain, applied per interface)",
            "Named Mode": "MD5 or SHA-256 HMAC (configured under af-interface sub-mode)",
          },
          exam_hot: true,
          notes: {
            "Classic Mode": "Classic mode is limited to MD5 authentication — no SHA support available",
            "Named Mode": "SHA-256 is only available in named mode — the exam expects you to know this distinction",
          },
        },
        {
          name: "IPv6 support",
          values: {
            "Classic Mode": "Separate 'ipv6 router eigrp <ASN>' process; IPv6 enabled per-interface with 'ipv6 eigrp <ASN>'",
            "Named Mode": "Native address-family ipv6 unicast under same named process; single unified config",
          },
          exam_hot: false,
          notes: {
            "Classic Mode": "Classic mode requires a completely separate EIGRP process for IPv6 — double the configuration",
            "Named Mode": "Named mode handles IPv4 and IPv6 under a single named process using address-family sub-modes",
          },
        },
        {
          name: "Wide metrics",
          values: {
            "Classic Mode": "Not supported — uses classic 32-bit composite metric (K1-K5)",
            "Named Mode": "Supported — 64-bit wide metrics (EIGRP metric version 64); accommodates interface speeds above 1 Gbps",
          },
          exam_hot: true,
          notes: {
            "Classic Mode": "Classic 32-bit metrics cannot differentiate between GigE, 10G, 40G, and 100G interfaces — all calculate the same cost",
            "Named Mode": "Wide metrics use 64-bit values to correctly differentiate high-speed interfaces; critical for modern networks with 10G+ links",
          },
        },
        {
          name: "Stub configuration",
          values: {
            "Classic Mode": "'eigrp stub' command under 'router eigrp <ASN>'",
            "Named Mode": "'eigrp stub' command under address-family sub-mode",
          },
          exam_hot: false,
          notes: {
            "Classic Mode": "Stub functionality is equivalent in both modes — reduces query scope and improves convergence",
            "Named Mode": "Same stub feature; syntax location differs — configured under address-family in named mode",
          },
        },
        {
          name: "Route summarization",
          values: {
            "Classic Mode": "'ip summary-address eigrp <ASN>' applied per interface",
            "Named Mode": "'summary-address' under af-interface sub-mode",
          },
          exam_hot: false,
          notes: {
            "Classic Mode": "Summary address is configured on each interface individually in classic mode",
            "Named Mode": "Named mode 'summary-address' is co-located with other af-interface settings — cleaner and more maintainable",
          },
        },
        {
          name: "Address-family support",
          values: {
            "Classic Mode": "Separate processes for IPv4 and IPv6; no VRF address-family sub-mode",
            "Named Mode": "Multiple address families (IPv4, IPv6) and VRFs under a single named process using address-family syntax",
          },
          exam_hot: true,
          notes: {
            "Classic Mode": "Classic mode requires separate processes for each address family — no VRF integration in the process hierarchy",
            "Named Mode": "Named mode mirrors BGP's address-family model — critical for multi-VRF and dual-stack deployments; this is an exam topic",
          },
        },
        {
          name: "Default config location",
          values: {
            "Classic Mode": "Global: 'router eigrp <ASN>'; interface: 'ip/ipv6 bandwidth-percent eigrp', 'ip hello-interval eigrp', etc.",
            "Named Mode": "All under 'router eigrp <name>' > 'address-family' > 'af-interface default' or specific interface sub-mode",
          },
          exam_hot: false,
          notes: {
            "Classic Mode": "Interface-level EIGRP commands like hello/hold timers are scattered across the running config in classic mode",
            "Named Mode": "'af-interface default' applies settings to all interfaces in the address family — reduces per-interface config repetition",
          },
        },
      ],
    },

    // -------------------------------------------------------------------------
    // cmp-019  EAP Types: PEAP vs EAP-TLS vs EAP-FAST
    // -------------------------------------------------------------------------
    {
      id: "cmp-019",
      title: "EAP Types: PEAP vs EAP-TLS vs EAP-FAST",
      domain: "5.0",
      protocols: ["PEAP", "EAP-TLS", "EAP-FAST"],
      is_custom: false,
      attributes: [
        {
          name: "Server certificate required",
          values: {
            PEAP: "Yes — server presents certificate to client to establish TLS tunnel",
            "EAP-TLS": "Yes — mutual TLS; server certificate mandatory",
            "EAP-FAST": "No — uses PAC (Protected Access Credential) instead; certificate optional",
          },
          exam_hot: false,
          notes: {
            PEAP: "PEAP requires the server (RADIUS/ISE) to present a certificate; the client validates it to build the TLS tunnel",
            "EAP-TLS": "Both server and client must have certificates — full mutual TLS authentication",
            "EAP-FAST": "EAP-FAST's PAC-based approach was designed for environments where PKI deployment is impractical",
          },
        },
        {
          name: "Client certificate required",
          values: {
            PEAP: "No — client authenticates inside TLS tunnel using MSCHAPv2 or other inner method (username/password)",
            "EAP-TLS": "Yes — mutual certificate authentication required; client must have a valid X.509 cert",
            "EAP-FAST": "No — client authenticates via PAC or username/password inside tunnel",
          },
          exam_hot: true,
          notes: {
            PEAP: "PEAP uses username/password inside the TLS tunnel — no client certificate needed; easiest to deploy at scale",
            "EAP-TLS": "Client certificates make EAP-TLS the most secure but operationally demanding — requires PKI for every endpoint",
            "EAP-FAST": "EAP-FAST uses PACs instead of client certificates — a middle ground between PEAP simplicity and EAP-TLS security",
          },
        },
        {
          name: "Inner authentication",
          values: {
            PEAP: "MSCHAPv2 (most common), EAP-GTC, or EAP-TLS inside the outer TLS tunnel",
            "EAP-TLS": "No inner method — the mutual certificate handshake IS the authentication",
            "EAP-FAST": "EAP-MSCHAPv2, EAP-GTC, or EAP-TLS inside the PAC-protected tunnel",
          },
          exam_hot: true,
          notes: {
            PEAP: "PEAP phase 1 = TLS tunnel establishment; phase 2 = inner auth (typically MSCHAPv2) — know this two-phase model",
            "EAP-TLS": "EAP-TLS has no inner/phase 2 method — the mutual certificate exchange IS the complete authentication",
            "EAP-FAST": "EAP-FAST inner methods are similar to PEAP but run inside a PAC-protected tunnel instead of a certificate-based tunnel",
          },
        },
        {
          name: "Credential type",
          values: {
            PEAP: "Username and password (Active Directory / LDAP integration common)",
            "EAP-TLS": "Digital certificate (X.509) on both client and server",
            "EAP-FAST": "PAC credential (provisioned automatically or manually) + optional username/password",
          },
          exam_hot: false,
          notes: {
            PEAP: "PEAP integrates easily with Active Directory/LDAP — the most common enterprise deployment model",
            "EAP-TLS": "X.509 certificates provide the strongest identity assurance — no passwords to steal or phish",
            "EAP-FAST": "PACs are pre-shared credentials provisioned during Phase 0 — stored securely on the client",
          },
        },
        {
          name: "PAC provisioning",
          values: {
            PEAP: "Not applicable",
            "EAP-TLS": "Not applicable",
            "EAP-FAST": "Phase 0: Anonymous in-band provisioning (unauthenticated) or authenticated provisioning; PAC stored on client",
          },
          exam_hot: false,
          notes: {
            PEAP: "",
            "EAP-TLS": "",
            "EAP-FAST": "Anonymous PAC provisioning (Phase 0) is vulnerable to man-in-the-middle attacks; authenticated provisioning uses a server certificate for safety",
          },
        },
        {
          name: "Cisco proprietary",
          values: {
            PEAP: "No — co-developed by Cisco, Microsoft, and RSA; IETF draft standard",
            "EAP-TLS": "No — open standard (RFC 5216)",
            "EAP-FAST": "Yes — Cisco proprietary (RFC 4851, but primarily a Cisco solution); designed to replace LEAP",
          },
          exam_hot: true,
          notes: {
            PEAP: "PEAP is a joint effort by Cisco, Microsoft, and RSA — widely supported across all major OS platforms",
            "EAP-TLS": "EAP-TLS is the open standard defined in RFC 5216 — the most secure EAP type but requires full PKI",
            "EAP-FAST": "EAP-FAST is Cisco proprietary (RFC 4851) — designed to replace the weak LEAP protocol; know it is Cisco-specific",
          },
        },
        {
          name: "Common deployment",
          values: {
            PEAP: "Enterprise 802.1X with Active Directory; most widely deployed EAP type",
            "EAP-TLS": "High-security environments requiring mutual authentication (finance, government); requires full PKI",
            "EAP-FAST": "Cisco-centric environments needing strong auth without full PKI; ISE deployments",
          },
          exam_hot: false,
          notes: {
            PEAP: "PEAP with MSCHAPv2 is the most common real-world 802.1X deployment due to ease of integration with AD",
            "EAP-TLS": "EAP-TLS is considered the most secure EAP type — used in high-security environments with mature PKI infrastructure",
            "EAP-FAST": "EAP-FAST is used in Cisco-heavy environments where PKI is not available but stronger-than-PEAP security is desired",
          },
        },
      ],
    },

    // -------------------------------------------------------------------------
    // cmp-020  NETCONF vs RESTCONF vs gNMI
    // -------------------------------------------------------------------------
    {
      id: "cmp-020",
      title: "NETCONF vs RESTCONF vs gNMI",
      domain: "6.0",
      protocols: ["NETCONF", "RESTCONF", "gNMI"],
      is_custom: false,
      attributes: [
        {
          name: "Transport protocol",
          values: {
            NETCONF: "SSH (TCP) — RFC 6242 mandates SSH as the mandatory transport",
            RESTCONF: "HTTPS (TLS over TCP) — HTTP/1.1 or HTTP/2",
            gNMI: "gRPC over HTTP/2 (TLS) — gRPC is the mandatory transport",
          },
          exam_hot: true,
          notes: {
            NETCONF: "NETCONF = SSH — memorize this association; it is a top-frequency exam question",
            RESTCONF: "RESTCONF = HTTPS — standard web transport; developer-friendly",
            gNMI: "gNMI = gRPC over HTTP/2 — the most modern and efficient transport of the three",
          },
        },
        {
          name: "Default port",
          values: {
            NETCONF: "TCP 830 (RFC 4742 originally used 22/SSH; 830 is the registered NETCONF port)",
            RESTCONF: "TCP 443 (HTTPS standard port; configured via ip http secure-server on IOS-XE)",
            gNMI: "TCP 57400 (Cisco IOS-XE default; not universally standardized)",
          },
          exam_hot: true,
          notes: {
            NETCONF: "Port 830 is consistently tested on ENCOR — know it cold",
            RESTCONF: "Port 443 is standard HTTPS — same port used by web services on the device",
            gNMI: "Port 57400 is Cisco's default for gNMI on IOS-XE; other vendors may use different ports",
          },
        },
        {
          name: "Data encoding",
          values: {
            NETCONF: "XML only",
            RESTCONF: "JSON or XML (Content-Type / Accept headers select format)",
            gNMI: "Protocol Buffers (protobuf) — binary encoding via gRPC; JSON_IETF also supported",
          },
          exam_hot: true,
          notes: {
            NETCONF: "NETCONF is XML-only — all RPC messages are XML-encoded; no JSON option",
            RESTCONF: "RESTCONF supports both JSON and XML — JSON is more common in practice and easier to work with programmatically",
            gNMI: "gNMI uses protobuf (binary) for efficiency — ideal for high-frequency telemetry data; JSON_IETF is the fallback",
          },
        },
        {
          name: "Operations / methods",
          values: {
            NETCONF: "get, get-config, edit-config, copy-config, delete-config, lock, unlock, commit, validate, close-session, kill-session",
            RESTCONF: "HTTP methods: GET, POST, PUT, PATCH, DELETE, OPTIONS — maps to CRUD operations",
            gNMI: "Capabilities, Get, Set, Subscribe (with stream modes: ONCE, POLL, STREAM)",
          },
          exam_hot: true,
          notes: {
            NETCONF: "NETCONF uses RPC operations; edit-config is the primary change operation; lock/unlock enable transactional edits",
            RESTCONF: "RESTCONF maps HTTP verbs to YANG operations: GET=read, POST=create, PUT=replace, PATCH=merge, DELETE=remove",
            gNMI: "gNMI Subscribe with STREAM mode is the primary mechanism for streaming telemetry — the key gNMI differentiator",
          },
        },
        {
          name: "YANG model support",
          values: {
            NETCONF: "Native YANG support; datastores: running, candidate, startup",
            RESTCONF: "YANG via RESTCONF API; resource-oriented URL path maps to YANG schema nodes",
            gNMI: "YANG model paths used for Get/Set/Subscribe operations (OpenConfig and native models)",
          },
          exam_hot: false,
          notes: {
            NETCONF: "NETCONF was designed hand-in-hand with YANG; it has the richest datastore support (running, candidate, startup)",
            RESTCONF: "RESTCONF URL paths directly reflect the YANG model hierarchy — e.g., /restconf/data/ietf-interfaces:interfaces",
            gNMI: "gNMI uses YANG-derived XPath-like paths for Get/Set/Subscribe — supports both OpenConfig and vendor-native models",
          },
        },
        {
          name: "Connection type",
          values: {
            NETCONF: "Persistent session (SSH session kept open; supports candidate datastore and transactions)",
            RESTCONF: "Stateless HTTP request/response (each request is independent)",
            gNMI: "Persistent gRPC stream (long-lived connection; especially for Subscribe)",
          },
          exam_hot: false,
          notes: {
            NETCONF: "Persistent SSH sessions enable lock/unlock and multi-step transactional configuration changes",
            RESTCONF: "Stateless HTTP means each request is independent — no session state, no locks, no candidate config",
            gNMI: "gRPC streams stay open for Subscribe operations — ideal for continuous telemetry data delivery",
          },
        },
        {
          name: "Subscription support (telemetry)",
          values: {
            NETCONF: "Yes — YANG Push (RFC 8639/8641); event notifications via <notification> messages",
            RESTCONF: "Yes — YANG Push (RFC 8639) over HTTP SSE (Server-Sent Events) or WebSockets; less common",
            gNMI: "Yes — native; Subscribe RPC with STREAM mode is the primary streaming telemetry mechanism for gNMI",
          },
          exam_hot: false,
          notes: {
            NETCONF: "NETCONF YANG Push (RFC 8639/8641) is the IETF standard approach to subscription-based telemetry",
            RESTCONF: "RESTCONF telemetry subscriptions via SSE exist but are less common than gNMI or NETCONF YANG Push",
            gNMI: "gNMI is THE protocol most associated with streaming telemetry in modern Cisco deployments — know this association",
          },
        },
        {
          name: "Standards body",
          values: {
            NETCONF: "IETF — RFC 6241 (NETCONF 1.1), RFC 6242 (over SSH)",
            RESTCONF: "IETF — RFC 8040",
            gNMI: "OpenConfig (open-source consortium led by major network operators); not a formal IETF standard",
          },
          exam_hot: false,
          notes: {
            NETCONF: "NETCONF is an IETF standard with a mature RFC ecosystem (6241, 6242, 7950 for YANG 1.1)",
            RESTCONF: "RESTCONF is defined by a single IETF RFC (8040) — simpler standards landscape than NETCONF",
            gNMI: "gNMI is an OpenConfig project, not IETF — Cisco implements it alongside IETF protocols on IOS-XE",
          },
        },
      ],
    },

    // -------------------------------------------------------------------------
    // cmp-021  Monitoring: Syslog vs SNMP Traps vs NetFlow vs Streaming Telemetry
    // -------------------------------------------------------------------------
    {
      id: "cmp-021",
      title: "Monitoring: Syslog vs SNMP Traps vs NetFlow vs Streaming Telemetry",
      domain: "4.0",
      protocols: ["Syslog", "SNMP Traps", "NetFlow", "Streaming Telemetry"],
      is_custom: false,
      attributes: [
        {
          name: "Data type",
          values: {
            Syslog: "Event/log messages — human-readable text describing device events, errors, and state changes",
            "SNMP Traps": "Discrete event notifications from MIB objects — structured but limited to SNMP-instrumented variables",
            NetFlow: "Traffic flow records — 5-tuple (src/dst IP, src/dst port, protocol) with byte/packet counters per flow",
            "Streaming Telemetry": "Structured operational data — any YANG-modeled metric (interface stats, CPU, routing table, etc.)",
          },
          exam_hot: true,
          notes: {
            Syslog: "Syslog provides human-readable event messages — great for troubleshooting but unstructured and hard to parse at scale",
            "SNMP Traps": "SNMP traps are structured MIB-based notifications — limited to what the MIB instruments",
            NetFlow: "NetFlow tracks traffic flows (5-tuple) — ideal for traffic analysis, capacity planning, and security forensics",
            "Streaming Telemetry": "Telemetry streams any YANG-modeled data — the most versatile and modern monitoring approach",
          },
        },
        {
          name: "Direction (push/pull)",
          values: {
            Syslog: "Push — device sends messages to syslog server asynchronously on event",
            "SNMP Traps": "Push — device initiates trap to NMS on event (Inform adds acknowledgment); polling (Get) is pull",
            NetFlow: "Push — device exports cached flow records to collector at interval or on flow expiry",
            "Streaming Telemetry": "Push — device streams data to collector based on subscription (dial-out) or collector connects (dial-in)",
          },
          exam_hot: true,
          notes: {
            Syslog: "Syslog is always push — the device sends messages asynchronously when events occur",
            "SNMP Traps": "Traps are push; SNMP GET/GETBULK polling is pull — the exam tests this push vs pull distinction",
            NetFlow: "NetFlow pushes flow records to the collector — the device initiates the export, not the collector",
            "Streaming Telemetry": "Dial-out (device pushes) is more common than dial-in (collector subscribes); both are push after subscription",
          },
        },
        {
          name: "Transport",
          values: {
            Syslog: "UDP 514 (default, unreliable) or TCP 6514 (RFC 5425, TLS-secured); UDP most common",
            "SNMP Traps": "UDP 162 (traps); SNMP Informs use UDP 162 with retransmission logic",
            NetFlow: "UDP (Cisco default, typically port 2055 or 9996); IPFIX can use TCP or SCTP",
            "Streaming Telemetry": "gRPC (TCP/TLS) for gNMI; NETCONF/NETCONF-YANG Push over SSH; RESTCONF over HTTPS",
          },
          exam_hot: true,
          notes: {
            Syslog: "UDP 514 is the default syslog port — unreliable transport; TCP 6514 adds TLS for secure syslog",
            "SNMP Traps": "UDP 162 is the standard trap destination port — Informs add acknowledgment but still use UDP 162",
            NetFlow: "NetFlow export uses UDP — flow records can be lost without notification; IPFIX adds TCP/SCTP options",
            "Streaming Telemetry": "Telemetry uses reliable transports (gRPC/TCP/TLS) — a major advantage over UDP-based legacy protocols",
          },
        },
        {
          name: "Configuration complexity",
          values: {
            Syslog: "Low — single 'logging host <IP>' command; severity levels 0–7",
            "SNMP Traps": "Moderate — requires SNMP community/user, trap-host config, and MIB knowledge",
            NetFlow: "Moderate — flow exporter, flow monitor, flow record, and interface-level activation required",
            "Streaming Telemetry": "Higher — requires YANG model knowledge, subscription config, and telemetry receiver/pipeline",
          },
          exam_hot: false,
          notes: {
            Syslog: "Syslog is the simplest monitoring to configure — just point to a server with 'logging host'",
            "SNMP Traps": "SNMP setup requires community strings (v1/v2c) or USM users (v3), plus specifying which traps to send",
            NetFlow: "Flexible NetFlow requires defining a flow record, flow monitor, flow exporter, and applying to interfaces",
            "Streaming Telemetry": "Telemetry config requires YANG model path knowledge and a receiver pipeline — highest initial complexity but best long-term value",
          },
        },
        {
          name: "Real-time capability",
          values: {
            Syslog: "Near real-time — messages sent on event occurrence with minimal delay",
            "SNMP Traps": "Near real-time — traps sent on threshold/event; polling intervals limit state visibility",
            NetFlow: "Delayed — flows exported after active timeout (default 60 s) or inactive timeout (default 15 s)",
            "Streaming Telemetry": "Real-time — sub-second streaming possible; periodic or on-change subscriptions",
          },
          exam_hot: false,
          notes: {
            Syslog: "Syslog messages are sent immediately on event — near real-time with minimal processing delay",
            "SNMP Traps": "Traps are near real-time for events; but SNMP polling (GET) has inherent delay based on poll interval",
            NetFlow: "NetFlow's cache-based export introduces delay — not suitable for real-time alerting on individual events",
            "Streaming Telemetry": "Sub-second streaming with on-change subscriptions provides the best real-time visibility of any monitoring method",
          },
        },
        {
          name: "Granularity",
          values: {
            Syslog: "Event-level — individual log message per event; no aggregation",
            "SNMP Traps": "Object-level — one trap per MIB variable event; limited to instrumented OIDs",
            NetFlow: "Flow-level — aggregated per-flow statistics; per-packet detail not available",
            "Streaming Telemetry": "High — any YANG leaf can be streamed; supports per-interface, per-process, per-path granularity",
          },
          exam_hot: false,
          notes: {
            Syslog: "Each syslog message describes one event — no aggregation or correlation built into the protocol",
            "SNMP Traps": "SNMP granularity is limited to what MIB OIDs are instrumented — cannot monitor arbitrary data",
            NetFlow: "NetFlow provides per-flow aggregate statistics — useful for traffic patterns but not per-packet inspection",
            "Streaming Telemetry": "Telemetry offers the finest granularity — any YANG-modeled leaf can be subscribed to and streamed",
          },
        },
        {
          name: "Standard protocol",
          values: {
            Syslog: "Yes — RFC 5424 (Syslog protocol); RFC 5425 (TLS transport)",
            "SNMP Traps": "Yes — SNMP v1 traps RFC 1157; SNMPv2c Informs RFC 1901; SNMPv3 RFC 3411–3418",
            NetFlow: "Cisco proprietary for v5/v9; IPFIX (RFC 7011) is the IETF standard equivalent",
            "Streaming Telemetry": "OpenConfig gNMI; IETF YANG Push (RFC 8639/8641); gRPC is an industry standard",
          },
          exam_hot: false,
          notes: {
            Syslog: "Syslog is well-standardized — RFC 5424 defines the message format; RFC 5425 adds TLS transport",
            "SNMP Traps": "SNMP is one of the oldest network management standards — multiple RFCs across v1, v2c, and v3",
            NetFlow: "NetFlow v5/v9 are Cisco proprietary; IPFIX (RFC 7011) is the IETF open standard equivalent — both tested on ENCOR",
            "Streaming Telemetry": "gNMI is OpenConfig (not IETF); YANG Push is IETF — both are valid telemetry standards in Cisco environments",
          },
        },
        {
          name: "DNA Center integration",
          values: {
            Syslog: "Partial — DNA Center can receive and correlate syslog for Network Assurance",
            "SNMP Traps": "Yes — DNA Center uses SNMP for device discovery and monitoring",
            NetFlow: "Yes — DNA Center consumes NetFlow/IPFIX for application visibility and traffic analysis",
            "Streaming Telemetry": "Yes — primary mechanism; DNA Center relies on streaming telemetry for real-time Assurance dashboards",
          },
          exam_hot: false,
          notes: {
            Syslog: "DNA Center can ingest syslog but it is not the primary data source for Assurance features",
            "SNMP Traps": "DNA Center uses SNMP for initial device discovery and basic health monitoring",
            NetFlow: "DNA Center consumes NetFlow/IPFIX data for application visibility and traffic analytics",
            "Streaming Telemetry": "Streaming telemetry is the primary data source for DNA Center (Catalyst Center) Assurance — the modern approach",
          },
        },
      ],
    },

    // -------------------------------------------------------------------------
    // cmp-022  HA: SSO vs NSF vs Graceful Restart vs BFD
    // -------------------------------------------------------------------------
    {
      id: "cmp-022",
      title: "HA: SSO vs NSF vs Graceful Restart vs BFD",
      domain: "1.0",
      protocols: ["SSO", "NSF", "Graceful Restart", "BFD"],
      is_custom: false,
      attributes: [
        {
          name: "Purpose",
          values: {
            SSO: "Stateful Switchover — synchronize control-plane state between active and standby supervisors so that a supervisor failover is transparent to the data plane",
            NSF: "Non-Stop Forwarding — continue forwarding packets using the existing FIB/CEF table during a control-plane restart or switchover, while routing protocols reconverge",
            "Graceful Restart": "Protocol-level extension (IETF standard per-protocol RFC) that allows a restarting router to signal neighbors to maintain adjacency and not withdraw routes during restart",
            BFD: "Bidirectional Forwarding Detection — sub-second failure detection for the data-plane path between two adjacent nodes; triggers routing protocol reconvergence faster than protocol hello timers",
          },
          exam_hot: true,
          notes: {
            SSO: "SSO = supervisor HA — keeps standby supervisor in hot-standby state with synchronized control-plane data",
            NSF: "NSF = forwarding continuity — the router keeps forwarding packets using stale CEF/FIB entries while the control plane restarts",
            "Graceful Restart": "GR = neighbor cooperation — the restarting router asks neighbors to hold adjacency and not withdraw routes during the restart window",
            BFD: "BFD = fast failure detection — sub-second detection triggers immediate routing protocol reconvergence; much faster than protocol hello timers",
          },
        },
        {
          name: "Layer of operation",
          values: {
            SSO: "Control plane (supervisor/RP redundancy); keeps line cards forwarding without interruption",
            NSF: "Control plane + data plane — preserves CEF/FIB for forwarding while control plane restarts",
            "Graceful Restart": "Control plane — routing protocol (OSPF, EIGRP, BGP, IS-IS) neighbor relationship preservation",
            BFD: "Data plane detection — sends rapid probe packets at configurable intervals; notifies control plane on failure",
          },
          exam_hot: false,
          notes: {
            SSO: "SSO operates at the supervisor/RP level — it synchronizes IOS state to the standby supervisor",
            NSF: "NSF bridges control and data planes — stale forwarding entries keep traffic flowing while protocols reconverge",
            "Graceful Restart": "GR is purely control plane — it preserves routing adjacencies and route information during a restart event",
            BFD: "BFD sends lightweight probe packets in the data plane; failure detection feeds back into the control plane for routing convergence",
          },
        },
        {
          name: "Requires dual supervisors",
          values: {
            SSO: "Yes — requires active + standby supervisor/RP; standby must be in hot-standby state",
            NSF: "Yes (with SSO) on Cisco platforms — NSF is typically enabled alongside SSO on dual-supervisor chassis",
            "Graceful Restart": "No — single-supervisor router can use GR; requires only that the restarting router supports the GR capability and neighbors cooperate",
            BFD: "No — BFD runs between any two directly connected (or multi-hop) neighbors; no hardware redundancy required",
          },
          exam_hot: true,
          notes: {
            SSO: "SSO requires a dual-supervisor chassis (e.g., Catalyst 9400, 9500-H, 6800) — a frequent exam discriminator",
            NSF: "NSF depends on SSO on Cisco platforms — both require dual supervisors for supervisor-level failover scenarios",
            "Graceful Restart": "GR works on any platform including single-supervisor routers — it only requires neighbor cooperation, not hardware redundancy",
            BFD: "BFD has no hardware redundancy requirement — it runs on any platform between any two BFD-enabled neighbors",
          },
        },
        {
          name: "Protocol dependency",
          values: {
            SSO: "Platform-level feature (IOS-XE hardware HA); protocol-agnostic but enables NSF/GR to work",
            NSF: "Works with SSO-capable platforms; routing protocols must support NSF awareness (OSPF, EIGRP, BGP, IS-IS NSF-aware mode)",
            "Graceful Restart": "Per-protocol: OSPF GR (RFC 3623 / Cisco proprietary), BGP GR (RFC 4724), EIGRP GR (named mode), IS-IS GR (RFC 5306)",
            BFD: "Protocol-independent detection; integrates with OSPF, EIGRP, BGP, IS-IS, HSRP, VRRP, and static routes as a client",
          },
          exam_hot: true,
          notes: {
            SSO: "SSO is platform-level — it enables other features (NSF, GR) but is not protocol-specific itself",
            NSF: "NSF requires NSF-aware neighbors — if a neighbor is not NSF-aware, it drops the adjacency during restart",
            "Graceful Restart": "Each routing protocol has its own GR implementation and RFC — OSPF RFC 3623, BGP RFC 4724, IS-IS RFC 5306",
            BFD: "BFD is protocol-independent — it acts as a service for any client protocol (OSPF, EIGRP, BGP, HSRP, static routes)",
          },
        },
        {
          name: "Convergence impact",
          values: {
            SSO: "Near-zero impact on data plane forwarding during supervisor switchover; routing protocol adjacencies are maintained",
            NSF: "Forwarding continues during convergence window (NSF timer: typically 30–90 s); stale routes may be used temporarily",
            "Graceful Restart": "Neighbors hold adjacency and do not re-advertise during restart period (GR timer); stale forwarding entries maintained until reconvergence",
            BFD: "Accelerates convergence by detecting failures in 50 ms–1 s (vs. protocol dead timers of 30–120 s); triggers immediate routing protocol reaction",
          },
          exam_hot: false,
          notes: {
            SSO: "SSO provides the most seamless supervisor failover — sub-second switchover with no data plane impact",
            NSF: "NSF keeps forwarding during the convergence window using stale CEF entries — traffic is not dropped during restart",
            "Graceful Restart": "GR depends on neighbor cooperation — neighbors maintain stale routes until the restarting router reconverges",
            BFD: "BFD detection at 150ms (50ms x 3) is dramatically faster than OSPF dead timer (40s) or BGP hold time (180s)",
          },
        },
        {
          name: "Peer awareness",
          values: {
            SSO: "Transparent to peers — neighbors see no change; SSO is entirely local to the device",
            NSF: "Requires NSF-aware neighbors — OSPF/BGP/EIGRP neighbors must support NSF helper mode to cooperate and not bring down the adjacency",
            "Graceful Restart": "Requires GR-capable helper neighbors — restarting router sends GR capability in hello/OPEN; helpers hold stale state during restart period",
            BFD: "Requires BFD on both endpoints — both neighbors must enable BFD; asymmetric BFD (one-way) is not supported",
          },
          exam_hot: false,
          notes: {
            SSO: "SSO is invisible to peers — the switchover happens internally and neighbors see no disruption",
            NSF: "NSF-aware neighbors run in helper mode — they maintain adjacency and routes while the restarting router recovers",
            "Graceful Restart": "GR helper neighbors must be explicitly configured or auto-negotiated — they hold stale routes until the GR timer expires",
            BFD: "BFD must be enabled on both ends of the link — unidirectional BFD is not supported and will not function",
          },
        },
        {
          name: "Timer range",
          values: {
            SSO: "Switchover time: typically < 1 s for supervisor failover on supported Catalyst platforms",
            NSF: "NSF restart timer: 30 s default (configurable); routing protocol must reconverge within this window",
            "Graceful Restart": "GR restart interval: OSPF default 120 s; BGP default 120 s; EIGRP follows NSF timer; configurable per protocol",
            BFD: "Hello interval: 50 ms – 999 ms (async mode); multiplier: 3 (default); detection time = interval × multiplier (minimum ~150 ms)",
          },
          exam_hot: false,
          notes: {
            SSO: "Sub-second supervisor switchover is typical on Catalyst 9000 series — the fastest HA mechanism",
            NSF: "NSF timer must be long enough for routing protocols to reconverge — 30s default may need tuning in large networks",
            "Graceful Restart": "GR timers are per-protocol and independently configurable; OSPF and BGP both default to 120s",
            BFD: "BFD minimum detection time of ~150ms (50ms x 3) is the fastest standard failure detection available in IOS-XE",
          },
        },
      ],
    },
    // -------------------------------------------------------------------------
    // cmp-023  LACP vs PAgP vs Static (EtherChannel)
    // -------------------------------------------------------------------------
    {
      id: "cmp-023",
      title: "LACP vs PAgP vs Static (EtherChannel)",
      domain: "3.0",
      protocols: ["LACP", "PAgP", "Static (mode on)"],
      is_custom: false,
      attributes: [
        {
          name: "Standard",
          values: {
            LACP: "IEEE 802.3ad (now 802.1AX)",
            PAgP: "Cisco proprietary",
            "Static (mode on)": "No protocol",
          },
          exam_hot: true,
          notes: {
            LACP: "LACP is the only standards-based EtherChannel protocol — prefer it in multi-vendor environments",
            PAgP: "PAgP is Cisco proprietary — only works between Cisco devices",
            "Static (mode on)": "No negotiation protocol — forces the channel without any verification of the other side",
          },
        },
        {
          name: "Modes",
          values: {
            LACP: "Active / Passive",
            PAgP: "Desirable / Auto",
            "Static (mode on)": "On (no negotiation)",
          },
          exam_hot: true,
          notes: {
            LACP: "At least one side must be Active for LACP to negotiate; Passive/Passive will NOT form a channel",
            PAgP: "At least one side must be Desirable; Auto/Auto will NOT form a channel — same logic as LACP Active/Passive",
            "Static (mode on)": "'mode on' forces the channel without negotiation — both sides must be 'on'; misconfig causes loops",
          },
        },
        {
          name: "Max Links",
          values: {
            LACP: "16 (8 active + 8 standby)",
            PAgP: "8 (no standby)",
            "Static (mode on)": "8",
          },
          exam_hot: true,
          notes: {
            LACP: "LACP supports 16 member links — 8 active + 8 hot-standby for automatic failover; a key exam differentiator",
            PAgP: "PAgP supports only 8 links with no standby capability",
            "Static (mode on)": "Static mode supports up to 8 links; no standby mechanism available",
          },
        },
        {
          name: "Negotiation",
          values: {
            LACP: "Yes — LACPDU every 30s (slow) or 1s (fast)",
            PAgP: "Yes — PAgP messages",
            "Static (mode on)": "None — forces channel without verification",
          },
          exam_hot: true,
          notes: {
            LACP: "LACPDUs verify link health and detect misconfig; fast rate (1s) provides quicker failure detection",
            PAgP: "PAgP negotiation messages verify compatibility before forming the channel",
            "Static (mode on)": "'mode on' bypasses all negotiation — will not detect mismatched VLAN, speed, or duplex settings; avoid in production",
          },
        },
        {
          name: "Hot-standby",
          values: {
            LACP: "Yes — excess links in standby state",
            PAgP: "No",
            "Static (mode on)": "No",
          },
          exam_hot: true,
          notes: {
            LACP: "LACP hot-standby links automatically activate when an active link fails — seamless redundancy",
            PAgP: "PAgP has no standby link capability — failed links reduce channel bandwidth until manually addressed",
            "Static (mode on)": "No standby support — same limitation as PAgP",
          },
        },
        {
          name: "Multi-vendor support",
          values: {
            LACP: "Yes",
            PAgP: "Cisco only",
            "Static (mode on)": "Yes (but risky)",
          },
          exam_hot: false,
          notes: {
            LACP: "LACP is the standard for multi-vendor EtherChannel — works with any 802.3ad-compliant switch",
            PAgP: "PAgP only works between Cisco devices — cannot form a channel with non-Cisco gear",
            "Static (mode on)": "Static mode works with any vendor but without negotiation — misconfig risk is high across vendors",
          },
        },
        {
          name: "Best practice",
          values: {
            LACP: "Recommended for all new deployments",
            PAgP: "Legacy Cisco environments",
            "Static (mode on)": "Lab/troubleshooting only",
          },
          exam_hot: false,
          notes: {
            LACP: "Cisco recommends LACP for all new EtherChannel deployments — standards-based with hot-standby support",
            PAgP: "PAgP is legacy — still functional but no advantages over LACP in modern networks",
            "Static (mode on)": "Static mode should only be used for troubleshooting or lab environments — never in production",
          },
        },
      ],
    },
    // -------------------------------------------------------------------------
    // cmp-024  NAT Types — Static vs Dynamic vs PAT
    // -------------------------------------------------------------------------
    {
      id: "cmp-024",
      title: "NAT Types — Static vs Dynamic vs PAT",
      domain: "3.0",
      protocols: ["Static NAT", "Dynamic NAT", "PAT (Overload)"],
      is_custom: false,
      attributes: [
        {
          name: "Mapping",
          values: {
            "Static NAT": "1:1 permanent (inside local ↔ inside global)",
            "Dynamic NAT": "1:1 from pool (temporary)",
            "PAT (Overload)": "Many:1 using port numbers",
          },
          exam_hot: true,
          notes: {
            "Static NAT": "Static NAT creates a permanent 1:1 mapping — the translation exists even when no traffic flows",
            "Dynamic NAT": "Dynamic NAT assigns addresses from a pool on a first-come-first-served basis — temporary until timeout",
            "PAT (Overload)": "PAT maps many inside hosts to one outside IP using unique source port numbers — the most common NAT type",
          },
        },
        {
          name: "Address translation",
          values: {
            "Static NAT": "Fixed mapping configured by admin",
            "Dynamic NAT": "First-come-first-served from pool",
            "PAT (Overload)": "Shares single IP, differentiates by source port (unique socket)",
          },
          exam_hot: true,
          notes: {
            "Static NAT": "The admin explicitly maps each inside local address to an inside global address — predictable and permanent",
            "Dynamic NAT": "Pool addresses are assigned dynamically — you cannot predict which inside host gets which outside IP",
            "PAT (Overload)": "PAT uses the source port to create unique translations — up to 65,535 simultaneous sessions per public IP",
          },
        },
        {
          name: "When translation expires",
          values: {
            "Static NAT": "Never (permanent)",
            "Dynamic NAT": "After idle timeout (24 hours default)",
            "PAT (Overload)": "After idle timeout (varies by protocol: TCP 24h, UDP 5min)",
          },
          exam_hot: true,
          notes: {
            "Static NAT": "Static entries persist in the NAT table indefinitely — even across reboots if saved in config",
            "Dynamic NAT": "Default 24-hour idle timeout; 'ip nat translation timeout' command to change",
            "PAT (Overload)": "PAT timeouts vary: TCP default 24h, UDP default 5min, DNS 1min — different protocols have different defaults",
          },
        },
        {
          name: "Pool exhaustion",
          values: {
            "Static NAT": "N/A (fixed)",
            "Dynamic NAT": "New connections DROPPED until pool entry frees",
            "PAT (Overload)": "Exhaustion rare — 65,535 ports available per IP",
          },
          exam_hot: true,
          notes: {
            "Static NAT": "No exhaustion possible — each mapping is preconfigured and permanent",
            "Dynamic NAT": "Pool exhaustion is a common exam scenario — traffic is silently dropped when no addresses are available",
            "PAT (Overload)": "Port exhaustion is rare but possible with ~65K concurrent sessions per IP — add more IPs if needed",
          },
        },
        {
          name: "Use case",
          values: {
            "Static NAT": "Servers needing inbound access (web, mail)",
            "Dynamic NAT": "Rarely used alone (limited pool)",
            "PAT (Overload)": "Internet access for internal networks",
          },
          exam_hot: false,
          notes: {
            "Static NAT": "Static NAT is required for servers that must be reachable from outside — DNS maps to the inside global address",
            "Dynamic NAT": "Dynamic NAT alone is rarely practical — pool exhaustion risk makes it unsuitable for large user populations",
            "PAT (Overload)": "PAT is by far the most common NAT type in real networks — hundreds or thousands of hosts share one public IP",
          },
        },
        {
          name: "Config keyword",
          values: {
            "Static NAT": "`ip nat inside source static`",
            "Dynamic NAT": "`ip nat inside source list ... pool ...`",
            "PAT (Overload)": "`ip nat inside source list ... interface ... overload`",
          },
          exam_hot: true,
          notes: {
            "Static NAT": "'ip nat inside source static <inside-local> <inside-global>' — simple 1:1 mapping command",
            "Dynamic NAT": "'ip nat inside source list <ACL> pool <name>' — the ACL defines which hosts get translated",
            "PAT (Overload)": "The 'overload' keyword is what makes it PAT vs dynamic NAT — without 'overload' it's dynamic NAT",
          },
        },
        {
          name: "NAT order of operations (inside→outside)",
          values: {
            "Static NAT": "Route THEN translate",
            "Dynamic NAT": "Route THEN translate",
            "PAT (Overload)": "Route THEN translate",
          },
          exam_hot: true,
          notes: {
            "Static NAT": "Inside→outside: routing decision first, then NAT translation — the source IP is translated after the routing lookup",
            "Dynamic NAT": "Same order as static: route first, translate second for inside-to-outside traffic",
            "PAT (Overload)": "Outside→inside reverses the order: NAT first, then routing — this reversal is an exam favorite",
          },
        },
      ],
    },
    // -------------------------------------------------------------------------
    // cmp-025  QoS Mechanisms — LLQ vs CBWFQ vs WFQ vs FIFO
    // -------------------------------------------------------------------------
    {
      id: "cmp-025",
      title: "QoS Mechanisms — LLQ vs CBWFQ vs WFQ vs FIFO",
      domain: "3.0",
      protocols: ["LLQ", "CBWFQ", "WFQ", "FIFO"],
      is_custom: false,
      attributes: [
        {
          name: "Full name",
          values: {
            LLQ: "Low Latency Queuing",
            CBWFQ: "Class-Based Weighted Fair Queuing",
            WFQ: "Weighted Fair Queuing",
            FIFO: "First In First Out",
          },
          exam_hot: false,
          notes: {
            LLQ: "LLQ = Low Latency Queuing — provides strict priority service for delay-sensitive traffic",
            CBWFQ: "CBWFQ = Class-Based Weighted Fair Queuing — provides bandwidth guarantees per traffic class",
            WFQ: "WFQ = Weighted Fair Queuing — automatic flow-based fairness using IP precedence weighting",
            FIFO: "FIFO = First In First Out — simplest queuing; no differentiation between traffic types",
          },
        },
        {
          name: "Classification",
          values: {
            LLQ: "Yes — class-map + priority queue",
            CBWFQ: "Yes — class-map + bandwidth guarantee",
            WFQ: "Automatic (flow-based hashing)",
            FIFO: "None — single queue",
          },
          exam_hot: true,
          notes: {
            LLQ: "LLQ uses class-maps to identify priority traffic — typically matching DSCP EF for voice",
            CBWFQ: "CBWFQ uses class-maps to match traffic classes and assign minimum bandwidth guarantees to each",
            WFQ: "WFQ automatically classifies flows using a hash of the 5-tuple — no manual classification needed",
            FIFO: "FIFO performs no classification — all packets enter a single queue regardless of type or priority",
          },
        },
        {
          name: "Guarantees",
          values: {
            LLQ: "Strict priority + policed bandwidth ceiling",
            CBWFQ: "Minimum bandwidth guarantee per class",
            WFQ: "Fair share proportional to IP precedence",
            FIFO: "None",
          },
          exam_hot: true,
          notes: {
            LLQ: "LLQ is the ONLY queuing mechanism providing strict priority — required for voice and real-time video",
            CBWFQ: "CBWFQ guarantees minimum bandwidth per class during congestion; classes can use excess bandwidth when available",
            WFQ: "WFQ provides proportional fairness weighted by IP precedence — higher precedence gets more bandwidth",
            FIFO: "FIFO provides no guarantees — large flows can starve small flows; tail-drop is the only congestion response",
          },
        },
        {
          name: "Starvation risk",
          values: {
            LLQ: "No — policed to prevent starving other classes",
            CBWFQ: "No — minimum guarantees protect each class",
            WFQ: "No — fair sharing prevents starvation",
            FIFO: "Yes — large flows dominate",
          },
          exam_hot: true,
          notes: {
            LLQ: "LLQ polices priority traffic to a configured rate — excess priority traffic is dropped to protect other classes",
            CBWFQ: "Each class has a guaranteed minimum bandwidth — no single class can starve others",
            WFQ: "WFQ's fair scheduling ensures all flows get proportional service — no single flow dominates",
            FIFO: "FIFO has no fairness mechanism — a single high-volume flow can consume all bandwidth and starve others",
          },
        },
        {
          name: "Best for",
          values: {
            LLQ: "Real-time traffic: voice (DSCP EF), video",
            CBWFQ: "Business-critical apps with bandwidth needs",
            WFQ: "Default queuing on slow links (<768 kbps)",
            FIFO: "Default on high-speed interfaces",
          },
          exam_hot: true,
          notes: {
            LLQ: "Voice traffic requires LLQ strict priority to meet latency requirements (<150ms one-way) and jitter targets",
            CBWFQ: "CBWFQ is ideal for business-critical applications that need guaranteed bandwidth but not strict priority",
            WFQ: "WFQ was the default queuing on slow WAN links; largely replaced by CBWFQ in modern deployments",
            FIFO: "FIFO is the default on high-speed interfaces (>768 kbps) where congestion is typically not an issue",
          },
        },
        {
          name: "Typical deployment",
          values: {
            LLQ: "Combined with CBWFQ: LLQ for voice, CBWFQ for data classes",
            CBWFQ: "Enterprise WAN QoS policies",
            WFQ: "Legacy — replaced by CBWFQ in modern networks",
            FIFO: "LAN interfaces (QoS typically not needed)",
          },
          exam_hot: false,
          notes: {
            LLQ: "LLQ and CBWFQ are always deployed together — LLQ handles priority traffic, CBWFQ handles everything else",
            CBWFQ: "CBWFQ is the standard for enterprise WAN QoS — used with MQC (Modular QoS CLI) policy-maps",
            WFQ: "WFQ is considered legacy — CBWFQ provides better control and is preferred in all modern deployments",
            FIFO: "FIFO is appropriate on LAN interfaces and high-speed WAN links where congestion is unlikely",
          },
        },
        {
          name: "DSCP markings served",
          values: {
            LLQ: "EF (46) — Expedited Forwarding",
            CBWFQ: "AF classes (AF11-AF43) — Assured Forwarding",
            WFQ: "All — weighted by IP Precedence/DSCP",
            FIFO: "All — no differentiation",
          },
          exam_hot: true,
          notes: {
            LLQ: "DSCP EF (46) is reserved for voice; LLQ strict priority ensures EF traffic gets immediate service",
            CBWFQ: "AF classes (AF11–AF43) provide four traffic classes with three drop precedences each — 12 codepoints total",
            WFQ: "WFQ weighs all traffic by IP precedence/DSCP value — higher values get proportionally more bandwidth",
            FIFO: "FIFO ignores all DSCP markings — every packet is treated identically regardless of marking",
          },
        },
      ],
    },
  ];
}
